import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Modal,
  PanResponder,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii, type V10Palette } from '../v10/theme';
import { Atmos, Chip, Field, GlassCard, GoldButton } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import {
  autoSchedulerPrerequisitesMet,
  clearListSchedules,
  fetchAutoScheduleLogs,
  forceRescheduleList,
  keepAllAutoScheduleLogs,
  keepAutoScheduleLog,
  manuallyRescheduleLoggedTask,
  parseETCToMinutes,
  scheduleListNow,
  scheduleListSubset,
  type AutoScheduleLogEntry,
  type TimeBlock,
} from '../lib/autoSchedule';
import { fetchTrash, permanentDelete, restoreFromTrash, type TrashItem } from '../lib/trash';

// ─────────────────────────────────────────────────────────────────────────
// Faithful native rebuild of the web KYRS ("Tasks") page — src/app/tasks/
// page.tsx + src/app/tasks/[listId]/page.tsx — adapted to a phone-width
// panel, INCLUDING the full auto-scheduler engine (src/lib/autoSchedule.ts
// is the single source of truth for the slot-finding algorithm; see that
// file's header for the exact web functions it ports and any deliberate
// divergences).
//
// Data model mirrors the web app exactly (same tables/columns):
//   task_lists: id, user_id, name, is_deletable, include_in_all_tasks_view,
//               categories, category_settings, auto_scheduler_enabled,
//               default_group_id, default_etc, olmps_sector, created_at,
//               updated_at
//   tasks:      id, user_id, list_id, title, done, group_id, sg1_id, sg2_id,
//               value_selections, etc, scheduled, scheduled_manual,
//               priority_add, tabled, category, order, notes, unlisted,
//               parent_task_id, created_at, updated_at
//   auto_schedule_log: user_id, task_id, task_title, scheduled_to, action,
//               reviewed, created_at
//   user_settings: groups (incl. scheduleActive/scheduleTimeBlocks),
//               value_settings, etc_intervals,
//               unscheduled_task_priority_bonus (read-only here — this is
//               the web's global Settings page data; mobile has no
//               equivalent settings editor yet, so we only *read* it to
//               power the same priority score + group/value pickers, and
//               the same group time-block scheduling rules).
//
// EXCLUSIONS (beyond the one explicitly allowed — drag gesture reordering)
// plus a couple of desktop-only, non-persisted chrome features that have no
// mobile analog:
//   • Column resizing & click-a-column-header-to-sort: this is desktop
//     mouse chrome with NO persisted backing state (columnWidths lives in
//     local React state only, never saved anywhere) — replaced with an
//     explicit sort control (Order / Title / Priority / Scheduled, tap to
//     flip direction) that preserves the actual sorting *feature*.
//   • Per-list "Visible Groups"/"Visible Value Columns" restriction: cosmetic
//     chrome that only narrows which chips are *offered* on web — the
//     underlying data (and the force-reschedule ghost-pruning that reads
//     these same columns) works identically without it. All active
//     groups/values are offered when editing a task; `scheduled` remains an
//     editable date/time string (identical DB semantics) with
//     Today/Tomorrow/Clear quick actions, alongside the full Auto-Scheduler
//     switch/defaults/Reschedule button and verification bell below.
//
// TRASH/UNDO (now ported, byte-compatible with web):
//   Deletes write the same `trash` table/columns as web's moveToTrash
//   (src/lib/trash.ts): source_component: 'Tasks', item_type: 'task',
//   original_table: 'tasks', full row snapshot in item_data — matching web's
//   src/app/tasks/[listId]/page.tsx handleDeleteTask exactly. A trashed task
//   is restorable from web's Settings → Trash page AND from the in-tool
//   Trash sheet added here (header trash icon); restore/permanent-delete go
//   through src/lib/trash.ts's restoreFromTrash/permanentDelete (mobile port
//   of the same web helpers). Web additionally keeps a session-only Cmd+Z
//   undo stack for instant keyboard undo — mobile has no keyboard, so
//   instead we surface a 5s inline "Deleted — Undo" bar after every delete
//   that calls the same restoreFromTrash path; functionally equivalent
//   (immediate one-tap restore), just a touch-appropriate affordance
//   instead of a hotkey.
//
// VERIFICATION BELL CALENDAR VIEW (now ported):
//   Adds a List/Calendar toggle mirroring web's AutoScheduleCalendarView —
//   a week grid (Sun–Sat columns × dayStartTime..dayEndTime rows, read from
//   user_settings.calendar_page, same table/columns as CalendarEditor.tsx)
//   showing calendar_events + other scheduled tasks as context, with the
//   pending verification entries highlighted and tappable for Keep/
//   Reschedule. Adapted to a single-column-at-a-time swipeable/paged layout
//   (phone width can't show 7 columns legibly) instead of web's dense
//   simultaneous 7-column grid — same underlying data and day-window/
//   interval config, same Keep/Reschedule actions, no functional loss.
// ─────────────────────────────────────────────────────────────────────────

// ---------- Types (mirror src/interfaces/settings.ts + task-utils.ts) -----

interface Criterion {
  id: string;
  title: string;
  score: number;
}

interface ValueSetting {
  id: string;
  name: string;
  isActive: boolean;
  criteria: Criterion[];
}

interface Group {
  id: string;
  title: string;
  groupNumber: number;
  parentId: string | null;
  isActive: boolean;
  scheduleActive?: boolean;
  scheduleTimeBlocks?: TimeBlock[];
}

interface ETCInterval {
  id: string;
  label: string;
}

interface CategorySetting {
  strikethrough?: boolean;
  includeInAllTasks?: boolean;
  includeInStats?: boolean;
  includeInAutoScheduler?: boolean;
}

interface TaskListRow {
  id: string;
  userId: string;
  name: string;
  isDeletable: boolean;
  includeInAllTasksView: boolean;
  categories: string[];
  categorySettings: Record<string, CategorySetting>;
  createdAt: string | null;
  autoSchedulerEnabled: boolean;
  defaultGroupId: string | null;
  defaultETC: string | null;
  olmpsSector: Sector | null;
}

interface Task {
  id: string;
  userId: string;
  listId: string;
  title: string;
  done: boolean;
  groupId: string | null;
  sg1Id: string | null;
  sg2Id: string | null;
  valueSelections: Record<string, string>;
  etc: string | null;
  scheduled: string | null;
  scheduledManual: boolean;
  priorityAdd: number | null;
  tabled: boolean;
  category: string | null;
  order: number | null;
  notes: string | null;
  unlisted: boolean;
  parentTaskId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

const TASKS_TABLE = 'tasks';
const TASK_LISTS_TABLE = 'task_lists';
const ALL_ACTIVE_ID = 'all-active-tasks';
const COMPLETED_ID = 'completed-tasks';
const UNCATEGORIZED_DRAFT_KEY = '__uncategorized__';

// ---------- Overview screen palette -----------------------------------------
// Originally a 1:1 port of the web tasks page palette; now mapped onto the v10
// theme so dark/light switch live. Used inside components and the style hook.
function webPalette(t: V10Palette) {
  return {
    PAGE_BG: t.night,
    FOREGROUND: t.ivory,
    PRIMARY: t.gold,
    MUTED: t.ivorySoft,
    BORDER: t.lineFaint,
    GREEN_BORDER: t.onPace,
    GREEN_TEXT: t.onPace,
    RED: t.lapsed,
  };
}

// ---------- Row mapping (snake_case DB ↔ camelCase app) --------------------

function mapList(row: any): TaskListRow {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name ?? '',
    isDeletable: row.is_deletable ?? true,
    includeInAllTasksView:
      row.include_in_all_tasks_view === undefined || row.include_in_all_tasks_view === null
        ? true
        : !!row.include_in_all_tasks_view,
    categories:
      Array.isArray(row.categories) && row.categories.length > 0 ? row.categories : ['Tabled'],
    categorySettings: row.category_settings ?? {},
    createdAt: row.created_at ?? null,
    autoSchedulerEnabled: !!row.auto_scheduler_enabled,
    defaultGroupId: row.default_group_id ?? null,
    defaultETC: row.default_etc ?? null,
    olmpsSector: (SECTORS as string[]).includes(row.olmps_sector) ? (row.olmps_sector as Sector) : null,
  };
}

function mapTask(row: any): Task {
  return {
    id: row.id,
    userId: row.user_id,
    listId: row.list_id,
    title: row.title ?? '',
    done: !!row.done,
    groupId: row.group_id ?? null,
    sg1Id: row.sg1_id ?? null,
    sg2Id: row.sg2_id ?? null,
    valueSelections: row.value_selections ?? {},
    etc: row.etc ?? null,
    scheduled: row.scheduled ?? null,
    scheduledManual: !!row.scheduled_manual,
    priorityAdd: row.priority_add ?? null,
    tabled: !!row.tabled,
    category: row.category ?? null,
    order: typeof row.order === 'number' ? row.order : null,
    notes: row.notes ?? null,
    unlisted: !!row.unlisted,
    parentTaskId: row.parent_task_id ?? null,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  };
}

// ---------- Priority score (mirrors calculateTaskPriorityLocal) -----------

interface PriorityInput {
  groupId: string | null;
  sg1Id: string | null;
  sg2Id: string | null;
  valueSelections: Record<string, string>;
  scheduled: string | null;
  priorityAdd: number | null;
}

function calcPriority(
  task: PriorityInput,
  groups: Group[],
  valueSettings: ValueSetting[],
  bonus: number
): number {
  let total = 0;
  const group = groups.find((g) => g.id === task.groupId);
  if (group) total += group.groupNumber;
  const sg1 = groups.find((g) => g.id === task.sg1Id);
  if (sg1) total += sg1.groupNumber;
  const sg2 = groups.find((g) => g.id === task.sg2Id);
  if (sg2) total += sg2.groupNumber;
  const activeValues = valueSettings.filter((v) => v.isActive);
  for (const [valueId, criterionId] of Object.entries(task.valueSelections || {})) {
    const vs = activeValues.find((v) => v.id === valueId);
    if (!vs) continue;
    const crit = vs.criteria.find((c) => c.id === criterionId);
    if (crit) total += crit.score;
  }
  if (!task.scheduled) total += bonus;
  if (task.priorityAdd) total += task.priorityAdd;
  return total;
}

function explainPriority(
  task: PriorityInput,
  groups: Group[],
  valueSettings: ValueSetting[],
  bonus: number
): Array<{ label: string; points: number }> {
  const parts: Array<{ label: string; points: number }> = [];
  const group = groups.find((g) => g.id === task.groupId);
  if (group) parts.push({ label: `Group · ${group.title}`, points: group.groupNumber });
  const sg1 = groups.find((g) => g.id === task.sg1Id);
  if (sg1) parts.push({ label: `Subgroup 1 · ${sg1.title}`, points: sg1.groupNumber });
  const sg2 = groups.find((g) => g.id === task.sg2Id);
  if (sg2) parts.push({ label: `Subgroup 2 · ${sg2.title}`, points: sg2.groupNumber });
  const activeValues = valueSettings.filter((v) => v.isActive);
  for (const [valueId, criterionId] of Object.entries(task.valueSelections || {})) {
    const vs = activeValues.find((v) => v.id === valueId);
    if (!vs) continue;
    const crit = vs.criteria.find((c) => c.id === criterionId);
    if (!crit) continue;
    parts.push({ label: `${vs.name} · ${crit.title}`, points: crit.score });
  }
  if (!task.scheduled) parts.push({ label: 'Unscheduled bonus', points: bonus });
  if (task.priorityAdd) parts.push({ label: 'Manual add', points: task.priorityAdd });
  return parts;
}

// ---------- Date helpers (no date-fns dependency in mobile) ---------------

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function dateStr(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseScheduledMillis(s?: string | null): number | null {
  if (!s) return null;
  let m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])).getTime();
  }
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  }
  const lower = s.toLowerCase().trim();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (lower === 'today') return today.getTime();
  if (lower === 'tomorrow') return today.getTime() + 86400000;
  if (lower === 'yesterday') return today.getTime() - 86400000;
  return null;
}

// All-numeric, matching web's `format(parsedDate, 'M/d h:mm a')` — e.g.
// "7/10 5:00 PM", never "Jul 10".
function formatScheduled(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return s;
  const label = `${Number(m[2])}/${Number(m[3])}`;
  if (m[4] !== undefined) {
    let hour = Number(m[4]);
    const minute = Number(m[5]);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${label} ${hour}:${pad2(minute)} ${suffix}`;
  }
  return label;
}

// Short-view cell: date only ("7/10") — the 44px "D" column can't fit a time.
function formatScheduledShort(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  return `${Number(m[2])}/${Number(m[3])}`;
}

// ---------- Badge description for a task card -----------------------------

interface Badge {
  key: string;
  label: string;
}

function describeBadges(task: Task, groups: Group[], valueSettings: ValueSetting[]): Badge[] {
  const out: Badge[] = [];
  if (task.scheduled) out.push({ key: 'sched', label: `📅 ${formatScheduled(task.scheduled)}` });
  if (task.etc) out.push({ key: 'etc', label: `⏱ ${task.etc}` });
  if (task.groupId) {
    const g = groups.find((x) => x.id === task.groupId);
    if (g) {
      let label = g.title;
      const sg1 = task.sg1Id ? groups.find((x) => x.id === task.sg1Id) : null;
      if (sg1) label += ` › ${sg1.title}`;
      const sg2 = task.sg2Id ? groups.find((x) => x.id === task.sg2Id) : null;
      if (sg2) label += ` › ${sg2.title}`;
      out.push({ key: 'group', label });
    }
  }
  for (const [valueId, criterionId] of Object.entries(task.valueSelections || {})) {
    const vs = valueSettings.find((v) => v.id === valueId);
    const crit = vs?.criteria.find((c) => c.id === criterionId);
    if (vs && crit) out.push({ key: `val-${valueId}`, label: `${vs.name}: ${crit.title}` });
  }
  if (task.notes) out.push({ key: 'notes', label: '📝 Note' });
  if (task.unlisted) out.push({ key: 'unlisted', label: 'Unlisted' });
  return out;
}

// ---------- Small UI atoms --------------------------------------------------

function ChipGroup({
  items,
  selectedId,
  onSelect,
  noneLabel = 'None',
  allowNone = true,
}: {
  items: Array<{ id: string; label: string }>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  noneLabel?: string;
  allowNone?: boolean;
}) {
  const styles = useStyles();
  return (
    <View style={styles.chipWrap}>
      {allowNone && <Chip label={noneLabel} active={selectedId === null} onPress={() => onSelect(null)} />}
      {items.map((it) => (
        <Chip key={it.id} label={it.label} active={selectedId === it.id} onPress={() => onSelect(it.id)} />
      ))}
    </View>
  );
}

function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  const styles = useStyles();
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      {children}
    </View>
  );
}

// ---------- Generic text-entry modal (rename list/category, add list…) ----

interface PromptState {
  title: string;
  message?: string;
  initial?: string;
  placeholder?: string;
  submitLabel?: string;
  onSubmit: (value: string) => void;
}

function PromptModal({
  state,
  onCancel,
}: {
  state: PromptState | null;
  onCancel: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [value, setValue] = useState('');

  useEffect(() => {
    if (state) setValue(state.initial ?? '');
  }, [state]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || !state) return;
    state.onSubmit(trimmed);
  };

  return (
    <Modal visible={!!state} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <GlassCard style={styles.promptBox}>
          <Text style={styles.promptTitle}>{state?.title}</Text>
          {!!state?.message && <Text style={styles.promptMessage}>{state.message}</Text>}
          <Field
            value={value}
            onChangeText={setValue}
            placeholder={state?.placeholder}
            autoFocus
            onSubmitEditing={submit}
            returnKeyType="done"
          />
          <View style={styles.promptButtons}>
            <GoldButton ghost label="Cancel" onPress={onCancel} />
            <GoldButton label={state?.submitLabel ?? 'Save'} onPress={submit} />
          </View>
        </GlassCard>
      </View>
    </Modal>
  );
}

// ---------- Auto-Scheduler: per-list settings modal ------------------------
// Native replacement for web's List Settings dialog's Auto-Scheduler
// section (src/app/tasks/page.tsx) — Switch + Default Group/ETC chips +
// Reschedule button, gated by the same prerequisites.

function AutoSchedulerModal({
  list,
  topGroups,
  etcIntervals,
  onCancel,
  onSave,
  onReschedule,
}: {
  list: TaskListRow | null;
  topGroups: Group[];
  etcIntervals: ETCInterval[];
  onCancel: () => void;
  onSave: (enabled: boolean, groupId: string | null, etc: string | null, sector: Sector | null) => void;
  onReschedule: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [enabled, setEnabled] = useState(false);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [etc, setEtc] = useState<string | null>(null);
  const [sector, setSector] = useState<Sector | null>(null);

  useEffect(() => {
    if (list) {
      setEnabled(list.autoSchedulerEnabled);
      setGroupId(list.defaultGroupId);
      setEtc(list.defaultETC);
      setSector(list.olmpsSector);
    }
  }, [list]);

  const prereqGroup = groupId ? topGroups.find((g) => g.id === groupId) : null;
  const prereqsMet = !!prereqGroup?.scheduleActive && !!prereqGroup?.scheduleTimeBlocks?.length && !!etc;

  return (
    <Modal visible={!!list} transparent animationType="slide" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <GlassCard style={styles.sheet}>
          <Text style={styles.sheetTitle}>Auto-Scheduler — {list?.name}</Text>
          <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
            <FormRow label="Auto-Scheduler">
              <Switch value={enabled && prereqsMet} onValueChange={setEnabled} trackColor={{ true: c.primary }} disabled={!prereqsMet} />
              {!prereqsMet && (
                <Text style={styles.helperText}>
                  Requires a Default Group that is Schedule Active with time slots, and a Default ETC.
                </Text>
              )}
            </FormRow>

            <FormRow label="Default Group">
              <ChipGroup
                items={topGroups.map((g) => ({ id: g.id, label: g.title }))}
                selectedId={groupId}
                onSelect={setGroupId}
                noneLabel="None"
              />
            </FormRow>

            <FormRow label="Default ETC">
              <ChipGroup
                items={etcIntervals.map((e) => ({ id: e.label, label: e.label }))}
                selectedId={etc}
                onSelect={setEtc}
                noneLabel="None"
              />
            </FormRow>

            <FormRow label="OLMPS Sector">
              <ChipGroup
                items={SECTORS.map((s) => ({ id: s, label: SECTOR_LABELS[s] }))}
                selectedId={sector}
                onSelect={(id) => setSector(id as Sector | null)}
                noneLabel="None"
              />
              <Text style={styles.helperText}>
                Every task in this list counts toward this sector on ASTRA.
              </Text>
            </FormRow>

            <Pressable
              style={[styles.rescheduleButton, (!list?.autoSchedulerEnabled) && styles.rescheduleButtonDisabled]}
              disabled={!list?.autoSchedulerEnabled}
              onPress={onReschedule}
            >
              <Text style={styles.rescheduleButtonText}>Reschedule this list now</Text>
            </Pressable>
          </ScrollView>

          <View style={styles.sheetFooter}>
            <GoldButton ghost label="Cancel" onPress={onCancel} />
            <GoldButton label="Save" onPress={() => onSave(enabled, groupId, etc, sector)} />
          </View>
        </GlassCard>
      </View>
    </Modal>
  );
}

// ---------- Trash sheet: Restore / Delete Forever (mirrors web's Settings →
// Trash page, scoped to source_component 'Tasks' / item_type 'task') -------

function formatTrashDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const label = `${d.getMonth() + 1}/${d.getDate()}`;
  let hour = d.getHours();
  const minute = d.getMinutes();
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${label}, ${hour}:${pad2(minute)} ${suffix}`;
}

function TrashModal({
  visible,
  items,
  loading,
  onClose,
  onRestore,
  onPermanentDelete,
  onEmptyAll,
}: {
  visible: boolean;
  items: TrashItem[];
  loading: boolean;
  onClose: () => void;
  onRestore: (item: TrashItem) => void;
  onPermanentDelete: (item: TrashItem) => void;
  onEmptyAll: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <GlassCard style={styles.sheet}>
          <Text style={styles.sheetTitle}>Trash</Text>
          <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
            {loading ? (
              <ActivityIndicator color={c.blueText} style={{ marginTop: 16 }} />
            ) : items.length === 0 ? (
              <Text style={styles.emptyText}>Trash is empty.</Text>
            ) : (
              <>
                <Pressable style={styles.promptBtn} onPress={onEmptyAll}>
                  <Text style={styles.promptBtnText}>Empty Trash ({items.length})</Text>
                </Pressable>
                {items.map((item) => (
                  <View key={item.id} style={styles.logRow}>
                    <View style={styles.logRowText}>
                      <Text style={styles.logTitle} numberOfLines={1}>
                        {item.item_title || 'Untitled task'}
                      </Text>
                      <Text style={styles.logMeta}>Deleted {formatTrashDate(item.deleted_at)}</Text>
                    </View>
                    <View style={styles.logRowActions}>
                      <Pressable style={styles.logActionBtn} onPress={() => onRestore(item)}>
                        <Text style={styles.logActionText}>Restore</Text>
                      </Pressable>
                      <Pressable style={styles.logActionBtn} onPress={() => onPermanentDelete(item)}>
                        <Text style={[styles.logActionText, styles.logActionTextDanger]}>Delete Forever</Text>
                      </Pressable>
                    </View>
                  </View>
                ))}
              </>
            )}
          </ScrollView>
          <View style={styles.sheetFooter}>
            <GoldButton label="Done" onPress={onClose} />
          </View>
        </GlassCard>
      </View>
    </Modal>
  );
}

// ---------- Auto-Scheduler: verification bell modal, List + Calendar view -

function formatLogDate(scheduledTo: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(scheduledTo);
  if (!m) return scheduledTo;
  const label = `${Number(m[2])}/${Number(m[3])}`;
  let hour = Number(m[4]);
  const minute = Number(m[5]);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${label} ${hour}:${pad2(minute)} ${suffix}`;
}

type VerificationView = 'list' | 'calendar';

function VerificationModal({
  visible,
  userId,
  logs,
  onClose,
  onKeep,
  onKeepAll,
  onReschedule,
}: {
  visible: boolean;
  userId: string;
  logs: AutoScheduleLogEntry[];
  onClose: () => void;
  onKeep: (log: AutoScheduleLogEntry) => void;
  onKeepAll: () => void;
  onReschedule: (log: AutoScheduleLogEntry) => void;
}) {
  const styles = useStyles();
  const [view, setView] = useState<VerificationView>('list');

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <GlassCard style={styles.sheet}>
          <View style={styles.verifHeaderRow}>
            <Text style={[styles.sheetTitle, { marginBottom: 0 }]}>Auto-Scheduled Verification</Text>
            <View style={styles.verifToggleRow}>
              <Chip small label="List" active={view === 'list'} onPress={() => setView('list')} />
              <Chip small label="Calendar" active={view === 'calendar'} onPress={() => setView('calendar')} />
            </View>
          </View>

          {logs.length === 0 ? (
            <Text style={[styles.emptyText, { paddingHorizontal: 16 }]}>No pending verifications.</Text>
          ) : view === 'list' ? (
            <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
              <Pressable style={styles.promptBtn} onPress={onKeepAll}>
                <Text style={styles.promptBtnText}>Keep All ({logs.length})</Text>
              </Pressable>
              {logs.map((log) => (
                <View key={log.id} style={styles.logRow}>
                  <View style={styles.logRowText}>
                    <Text style={styles.logTitle} numberOfLines={1}>
                      {log.task_title}
                    </Text>
                    <Text style={styles.logMeta}>
                      {formatLogDate(log.scheduled_to)} · {log.action === 'auto_rescheduled' ? 'rescheduled' : 'scheduled'}
                    </Text>
                  </View>
                  <View style={styles.logRowActions}>
                    <Pressable style={styles.logActionBtn} onPress={() => onKeep(log)}>
                      <Text style={styles.logActionText}>Keep</Text>
                    </Pressable>
                    <Pressable style={styles.logActionBtn} onPress={() => onReschedule(log)}>
                      <Text style={styles.logActionText}>Reschedule</Text>
                    </Pressable>
                  </View>
                </View>
              ))}
            </ScrollView>
          ) : (
            <VerificationCalendarView userId={userId} logs={logs} onKeep={onKeep} onReschedule={onReschedule} />
          )}

          <View style={styles.sheetFooter}>
            <GoldButton label="Done" onPress={onClose} />
          </View>
        </GlassCard>
      </View>
    </Modal>
  );
}

// ---------- Verification bell: Calendar view --------------------------------
// Mirrors web's AutoScheduleCalendarView (src/app/tasks/page.tsx): same data
// sources (calendar_events + other scheduled tasks as read-only context) and
// the same day-window/interval config (user_settings.calendar_page — same
// table/columns CalendarEditor.tsx reads/writes). Rendered as one day at a
// time with Prev/Today/Next day paging (a 7-day-wide simultaneous grid does
// not fit legibly on a phone) instead of web's 7-column week grid; pending
// verification entries are highlighted the same way and tappable for the
// same Keep/Reschedule actions.

interface CalendarPageConfigLite {
  dayStartTime: string;
  dayEndTime: string;
  timeIntervalMinutes: 30 | 60;
  timeFormat: '12hr' | '24hr';
}

const CALENDAR_CONFIG_FALLBACK: CalendarPageConfigLite = {
  dayStartTime: '06:00',
  dayEndTime: '22:00',
  timeIntervalMinutes: 30,
  timeFormat: '12hr',
};

interface VerifCalItem {
  id: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  isVerification: boolean;
  log?: AutoScheduleLogEntry;
}

function parseTimeToMinutes(t: string): number {
  if (!t || !t.includes(':')) return 0;
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function formatSlotLabel(t: string, fmt: '12hr' | '24hr'): string {
  if (fmt === '24hr') return t;
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${pad2(m)} ${suffix}`;
}

function VerificationCalendarView({
  userId,
  logs,
  onKeep,
  onReschedule,
}: {
  userId: string;
  logs: AutoScheduleLogEntry[];
  onKeep: (log: AutoScheduleLogEntry) => void;
  onReschedule: (log: AutoScheduleLogEntry) => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [config, setConfig] = useState<CalendarPageConfigLite>(CALENDAR_CONFIG_FALLBACK);
  const [dayOffset, setDayOffset] = useState(0);
  const [items, setItems] = useState<VerifCalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AutoScheduleLogEntry | null>(null);

  const targetDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + dayOffset);
    return d;
  }, [dayOffset]);
  const targetDateStr = useMemo(() => dateStr(dayOffset), [dayOffset]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_settings')
        .select('calendar_page')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      const cfg = (data?.calendar_page ?? {}) as Partial<CalendarPageConfigLite>;
      if (cfg.dayStartTime && cfg.dayEndTime && parseTimeToMinutes(cfg.dayEndTime) > parseTimeToMinutes(cfg.dayStartTime)) {
        setConfig({
          dayStartTime: cfg.dayStartTime,
          dayEndTime: cfg.dayEndTime,
          timeIntervalMinutes: cfg.timeIntervalMinutes === 60 ? 60 : 30,
          timeFormat: cfg.timeFormat === '24hr' ? '24hr' : '12hr',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const verificationTaskIds = useMemo(() => new Set(logs.map((l) => l.task_id)), [logs]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const out: VerifCalItem[] = [];
      const [{ data: events }, { data: tasks }] = await Promise.all([
        supabase.from('calendar_events').select('*').eq('user_id', userId).eq('date', targetDateStr),
        supabase.from(TASKS_TABLE).select('*').eq('user_id', userId).eq('done', false).not('scheduled', 'is', null),
      ]);
      (events ?? []).forEach((e: any) => {
        if (e.repeat && e.repeat !== 'none') return;
        if (e.title && e.date && e.start_time && e.end_time) {
          out.push({ id: e.id, title: e.title, date: e.date, startTime: e.start_time, endTime: e.end_time, isVerification: false });
        }
      });
      (tasks ?? []).forEach((row: any) => {
        if (verificationTaskIds.has(row.id)) return;
        const scheduled: string | null = row.scheduled ?? null;
        if (!scheduled) return;
        const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(scheduled);
        if (!m || m[1] !== targetDateStr) return;
        const startMin = parseTimeToMinutes(m[2]);
        const durMin = parseETCToMinutes(row.etc) ?? 30;
        const endMin = startMin + durMin;
        out.push({
          id: `task-${row.id}`,
          title: row.title ?? '',
          date: m[1],
          startTime: m[2],
          endTime: `${pad2(Math.floor(endMin / 60) % 24)}:${pad2(endMin % 60)}`,
          isVerification: false,
        });
      });
      logs.forEach((log) => {
        const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(log.scheduled_to);
        if (!m || m[1] !== targetDateStr) return;
        const startMin = parseTimeToMinutes(m[2]);
        const endMin = startMin + 30;
        out.push({
          id: `verif-${log.id}`,
          title: log.task_title,
          date: m[1],
          startTime: m[2],
          endTime: `${pad2(Math.floor(endMin / 60) % 24)}:${pad2(endMin % 60)}`,
          isVerification: true,
          log,
        });
      });
      if (!cancelled) {
        setItems(out);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, targetDateStr, logs, verificationTaskIds]);

  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    const start = parseTimeToMinutes(config.dayStartTime);
    let end = parseTimeToMinutes(config.dayEndTime);
    if (end === 0 && start > 0) end = 24 * 60;
    if (end <= start) return slots;
    for (let m = start; m < end; m += config.timeIntervalMinutes) {
      slots.push(`${pad2(Math.floor(m / 60) % 24)}:${pad2(m % 60)}`);
    }
    return slots;
  }, [config]);

  const dayLogsCount = items.filter((i) => i.isVerification).length;

  return (
    <View style={styles.calWrap}>
      <View style={styles.calNavRow}>
        <Pressable style={styles.calNavBtn} onPress={() => setDayOffset((d) => d - 1)}>
          <Text style={styles.calNavBtnText}>‹</Text>
        </Pressable>
        <Pressable style={styles.calTodayBtn} onPress={() => setDayOffset(0)}>
          <Text style={styles.calTodayBtnText}>Today</Text>
        </Pressable>
        <Pressable style={styles.calNavBtn} onPress={() => setDayOffset((d) => d + 1)}>
          <Text style={styles.calNavBtnText}>›</Text>
        </Pressable>
        <Text style={styles.calDateLabel}>
          {`${targetDate.getMonth() + 1}/${targetDate.getDate()}`}
          {dayLogsCount > 0 ? ` · ${dayLogsCount} pending` : ''}
        </Text>
      </View>

      {selectedLog && (
        <View style={styles.calSelectedRow}>
          <Text style={styles.calSelectedTitle} numberOfLines={1}>
            {selectedLog.task_title}
          </Text>
          <View style={styles.logRowActions}>
            <Pressable
              style={styles.logActionBtn}
              onPress={() => {
                onKeep(selectedLog);
                setSelectedLog(null);
              }}
            >
              <Text style={styles.logActionText}>Keep</Text>
            </Pressable>
            <Pressable
              style={styles.logActionBtn}
              onPress={() => {
                onReschedule(selectedLog);
                setSelectedLog(null);
              }}
            >
              <Text style={styles.logActionText}>Reschedule</Text>
            </Pressable>
            <Pressable style={styles.logActionBtn} onPress={() => setSelectedLog(null)}>
              <Text style={styles.logActionText}>✕</Text>
            </Pressable>
          </View>
        </View>
      )}

      <ScrollView style={styles.calSlotScroll} contentContainerStyle={styles.calSlotScrollContent}>
        {loading ? (
          <ActivityIndicator color={c.blueText} style={{ marginTop: 16 }} />
        ) : timeSlots.length === 0 ? (
          <Text style={styles.emptyText}>Calendar config invalid — check Settings.</Text>
        ) : (
          timeSlots.map((slot) => {
            const slotMin = parseTimeToMinutes(slot);
            const inSlot = items.filter(
              (i) => parseTimeToMinutes(i.startTime) >= slotMin && parseTimeToMinutes(i.startTime) < slotMin + config.timeIntervalMinutes
            );
            return (
              <View key={slot} style={styles.calSlotRow}>
                <Text style={styles.calSlotLabel}>{formatSlotLabel(slot, config.timeFormat)}</Text>
                <View style={styles.calSlotItems}>
                  {inSlot.length === 0 ? (
                    <View style={styles.calSlotEmpty} />
                  ) : (
                    inSlot.map((i) =>
                      i.isVerification && i.log ? (
                        <Pressable
                          key={i.id}
                          style={styles.calItemVerif}
                          onPress={() => setSelectedLog(i.log!)}
                        >
                          <Text style={styles.calItemVerifText} numberOfLines={1}>
                            {i.title}
                          </Text>
                        </Pressable>
                      ) : (
                        <View key={i.id} style={styles.calItem}>
                          <Text style={styles.calItemText} numberOfLines={1}>
                            {i.title}
                          </Text>
                        </View>
                      )
                    )
                  )}
                </View>
              </View>
            );
          })
        )}
      </ScrollView>
    </View>
  );
}

// ---------- Manual date/time entry for a logged entry's Reschedule action -

function LogRescheduleModal({
  log,
  onCancel,
  onSubmit,
}: {
  log: AutoScheduleLogEntry | null;
  onCancel: () => void;
  onSubmit: (dateStr: string, timeStr: string) => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  useEffect(() => {
    if (log) {
      const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(log.scheduled_to);
      setDate(m ? m[1] : dateStr(0));
      setTime(m ? m[2] : '09:00');
    }
  }, [log]);

  return (
    <Modal visible={!!log} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.backdrop}>
        <GlassCard style={styles.promptBox}>
          <Text style={styles.promptTitle}>Reschedule "{log?.task_title}"</Text>
          <FormRow label="Date (yyyy-MM-dd)">
            <Field value={date} onChangeText={setDate} placeholder="yyyy-MM-dd" />
          </FormRow>
          <View style={styles.chipWrap}>
            <Chip label="Today" active={false} onPress={() => setDate(dateStr(0))} />
            <Chip label="Tomorrow" active={false} onPress={() => setDate(dateStr(1))} />
          </View>
          <FormRow label="Time (HH:mm)">
            <Field value={time} onChangeText={setTime} placeholder="09:00" />
          </FormRow>
          <View style={styles.promptButtons}>
            <GoldButton ghost label="Cancel" onPress={onCancel} />
            <GoldButton label="Save" onPress={() => onSubmit(date.trim(), time.trim())} />
          </View>
        </GlassCard>
      </View>
    </Modal>
  );
}

// ---------- Add/Edit Task modal (every field the web edits) ---------------

interface TaskFormValue {
  title: string;
  groupId: string | null;
  sg1Id: string | null;
  sg2Id: string | null;
  valueSelections: Record<string, string>;
  etc: string | null;
  scheduled: string | null;
  priorityAdd: number | null;
  notes: string | null;
  category: string | null;
  unlisted: boolean;
  parentTaskId: string | null;
  moveToListId: string | null;
  done: boolean;
}

function buildInitialForm(
  task: Task | null,
  presetCategory: string | null,
  presetParentId: string | null
): TaskFormValue {
  if (task) {
    return {
      title: task.title,
      groupId: task.groupId,
      sg1Id: task.sg1Id,
      sg2Id: task.sg2Id,
      valueSelections: { ...task.valueSelections },
      etc: task.etc,
      scheduled: task.scheduled,
      priorityAdd: task.priorityAdd,
      notes: task.notes,
      category: task.category,
      unlisted: task.unlisted,
      parentTaskId: task.parentTaskId,
      moveToListId: null,
      done: task.done,
    };
  }
  return {
    title: '',
    groupId: null,
    sg1Id: null,
    sg2Id: null,
    valueSelections: {},
    etc: null,
    scheduled: null,
    priorityAdd: null,
    notes: null,
    category: presetCategory,
    unlisted: false,
    parentTaskId: presetParentId,
    moveToListId: null,
    done: false,
  };
}

function TaskFormModal({
  visible,
  isEditing,
  initial,
  categories,
  topGroups,
  allGroups,
  valueSettings,
  etcIntervals,
  parentOptions,
  otherLists,
  onCancel,
  onSave,
  onDelete,
}: {
  visible: boolean;
  isEditing: boolean;
  initial: TaskFormValue;
  categories: string[];
  topGroups: Group[];
  allGroups: Group[];
  valueSettings: ValueSetting[];
  etcIntervals: ETCInterval[];
  parentOptions: Array<{ id: string; label: string }>;
  otherLists: Array<{ id: string; label: string }>;
  onCancel: () => void;
  onSave: (value: TaskFormValue) => void;
  onDelete?: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [form, setForm] = useState<TaskFormValue>(initial);
  const [priorityAddText, setPriorityAddText] = useState('');

  useEffect(() => {
    if (visible) {
      setForm(initial);
      setPriorityAddText(initial.priorityAdd !== null ? String(initial.priorityAdd) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const sg1Options = useMemo(
    () => (form.groupId ? allGroups.filter((g) => g.isActive && g.parentId === form.groupId) : []),
    [allGroups, form.groupId]
  );
  const sg2Options = useMemo(
    () => (form.sg1Id ? allGroups.filter((g) => g.isActive && g.parentId === form.sg1Id) : []),
    [allGroups, form.sg1Id]
  );

  const setValueSelection = (valueId: string, criterionId: string | null) => {
    setForm((f) => {
      const next = { ...f.valueSelections };
      if (criterionId === null) delete next[valueId];
      else next[valueId] = criterionId;
      return { ...f, valueSelections: next };
    });
  };

  const save = () => {
    const title = form.title.trim();
    if (!title) {
      Alert.alert('Title required', 'Please enter a task title.');
      return;
    }
    const parsedAdd =
      priorityAddText.trim() === '' ? null : Number(priorityAddText.replace(/[^0-9.-]/g, ''));
    onSave({
      ...form,
      title,
      priorityAdd: parsedAdd !== null && !Number.isNaN(parsedAdd) ? parsedAdd : null,
    });
  };

  const confirmDelete = () => {
    if (!onDelete) return;
    Alert.alert('Delete task?', form.title || 'Untitled task', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: onDelete },
    ]);
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <KeyboardAvoidingView
        style={styles.backdrop}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <GlassCard style={styles.sheet}>
          <Text style={styles.sheetTitle}>{isEditing ? 'Edit Task' : 'Add Task'}</Text>
          <ScrollView style={styles.sheetScroll} contentContainerStyle={styles.sheetScrollContent}>
            <FormRow label="Title">
              <Field
                value={form.title}
                onChangeText={(v) => setForm((f) => ({ ...f, title: v }))}
                placeholder="Task title"
                autoFocus={!isEditing}
              />
            </FormRow>

            {isEditing && (
              <FormRow label="Completed">
                <Switch
                  value={form.done}
                  onValueChange={(v) => setForm((f) => ({ ...f, done: v }))}
                  trackColor={{ true: c.primary }}
                />
              </FormRow>
            )}

            <FormRow label="Category / section">
              <ChipGroup
                items={categories.map((c) => ({ id: c, label: c }))}
                selectedId={form.category}
                onSelect={(v) => setForm((f) => ({ ...f, category: v }))}
                noneLabel="Active"
              />
            </FormRow>

            <FormRow label="Group">
              <ChipGroup
                items={topGroups.map((g) => ({ id: g.id, label: g.title }))}
                selectedId={form.groupId}
                onSelect={(v) => setForm((f) => ({ ...f, groupId: v, sg1Id: null, sg2Id: null }))}
              />
            </FormRow>

            {form.groupId && sg1Options.length > 0 && (
              <FormRow label="Subgroup 1">
                <ChipGroup
                  items={sg1Options.map((g) => ({ id: g.id, label: g.title }))}
                  selectedId={form.sg1Id}
                  onSelect={(v) => setForm((f) => ({ ...f, sg1Id: v, sg2Id: null }))}
                />
              </FormRow>
            )}

            {form.sg1Id && sg2Options.length > 0 && (
              <FormRow label="Subgroup 2">
                <ChipGroup
                  items={sg2Options.map((g) => ({ id: g.id, label: g.title }))}
                  selectedId={form.sg2Id}
                  onSelect={(v) => setForm((f) => ({ ...f, sg2Id: v }))}
                />
              </FormRow>
            )}

            {valueSettings.map((vs) => (
              <FormRow key={vs.id} label={vs.name}>
                <ChipGroup
                  items={vs.criteria.map((c) => ({ id: c.id, label: `${c.title} (${c.score})` }))}
                  selectedId={form.valueSelections[vs.id] ?? null}
                  onSelect={(v) => setValueSelection(vs.id, v)}
                />
              </FormRow>
            ))}

            <FormRow label="ETC (duration)">
              <Field
                value={form.etc ?? ''}
                onChangeText={(v) => setForm((f) => ({ ...f, etc: v.trim() === '' ? null : v }))}
                placeholder="e.g. 30 min, 1 hour"
              />
              {etcIntervals.length > 0 && (
                <View style={styles.chipWrap}>
                  {etcIntervals.map((e) => (
                    <Chip
                      key={e.id}
                      label={e.label}
                      active={form.etc === e.label}
                      onPress={() => setForm((f) => ({ ...f, etc: e.label }))}
                    />
                  ))}
                </View>
              )}
            </FormRow>

            <FormRow label="Scheduled">
              <Field
                value={form.scheduled ?? ''}
                onChangeText={(v) => setForm((f) => ({ ...f, scheduled: v.trim() === '' ? null : v }))}
                placeholder="yyyy-MM-dd HH:mm"
              />
              <View style={styles.chipWrap}>
                <Chip label="Today" active={false} onPress={() => setForm((f) => ({ ...f, scheduled: dateStr(0) }))} />
                <Chip
                  label="Tomorrow"
                  active={false}
                  onPress={() => setForm((f) => ({ ...f, scheduled: dateStr(1) }))}
                />
                <Chip label="Clear" active={false} onPress={() => setForm((f) => ({ ...f, scheduled: null }))} />
              </View>
            </FormRow>

            <FormRow label="Priority add (manual)">
              <Field
                value={priorityAddText}
                onChangeText={setPriorityAddText}
                placeholder="0"
                keyboardType="numbers-and-punctuation"
              />
            </FormRow>

            <FormRow label="Notes">
              <Field
                inputStyle={styles.textArea}
                value={form.notes ?? ''}
                onChangeText={(v) => setForm((f) => ({ ...f, notes: v.trim() === '' ? null : v }))}
                placeholder="Add a note…"
                multiline
              />
            </FormRow>

            <FormRow label="Unlisted (excluded from counts & scheduling)">
              <Switch
                value={form.unlisted}
                onValueChange={(v) => setForm((f) => ({ ...f, unlisted: v }))}
                trackColor={{ true: c.primary }}
              />
            </FormRow>

            {parentOptions.length > 0 && (
              <FormRow label="Parent task (make this a subtask)">
                <ChipGroup
                  items={parentOptions}
                  selectedId={form.parentTaskId}
                  onSelect={(v) => setForm((f) => ({ ...f, parentTaskId: v }))}
                  noneLabel="None (top-level)"
                />
              </FormRow>
            )}

            {isEditing && otherLists.length > 0 && (
              <FormRow label="Move to list">
                <ChipGroup
                  items={otherLists}
                  selectedId={form.moveToListId}
                  onSelect={(v) => setForm((f) => ({ ...f, moveToListId: v }))}
                  noneLabel="Keep in current list"
                />
              </FormRow>
            )}

            {isEditing && onDelete && (
              <Pressable style={styles.deleteRow} onPress={confirmDelete}>
                <Text style={styles.deleteRowText}>Delete Task</Text>
              </Pressable>
            )}
          </ScrollView>

          <View style={styles.sheetFooter}>
            <GoldButton ghost label="Cancel" onPress={onCancel} />
            <GoldButton label="Save" onPress={save} />
          </View>
        </GlassCard>
      </KeyboardAvoidingView>
    </Modal>
  );
}

// ---------- Task card -------------------------------------------------------

// ---------- Task table (1:1 structural port of web's src/app/tasks/
// [listId]/page.tsx renderTableHeader()/renderTaskRow() — flat shadcn
// <Table>, NOT chips/cards). Column set/order/conditions mirror the web
// function exactly: drag-handle spacer, Done, Scheduled, Title, one column
// per active top-level Group, SG1/SG2 (only when there is at least one
// group column), one column per active Value setting, ETC, Priority,
// Priority "Add", Actions. Web's per-list "Visible Groups"/"Visible Value
// Columns" cosmetic restriction is not ported (see file header note) — every
// active group/value setting gets a column here.
//
// Web edits Group/SG1/SG2/ETC/Value cells with an inline shadcn <Select>
// (a mouse dropdown). React Native has no native equivalent and no new
// deps may be added, so each of those cells is a flat, bordered Pressable
// that opens a native Alert action-sheet with the same option set (current
// value highlighted with a leading check) — same field, same persistence,
// touch-appropriate control instead of a mouse dropdown.
// Group/SG1/SG2 columns are deliberately NOT rendered on mobile (they ate all
// the horizontal space); group/subgroup editing lives in the Edit Task sheet.
// Short vs Long view mirrors web's viewMode toggle: short shows only
// drag/Done/D(date)/Title/Actions, long adds the value columns + ETC + Pri +
// Add — same columns web hides behind `viewMode === 'long'`.
type TableViewMode = 'short' | 'long';

const COL = {
  drag: 28,
  done: 34,
  sched: 96,
  schedShort: 44,
  title: 168,
  value: 76,
  etc: 84,
  priority: 50,
  add: 52,
  actions: 34,
} as const;

// Title column is user-resizable via a drag handle in its header (see
// titleWidth state) — clamp so it can't collapse to nothing or run away.
const TITLE_MIN = 96;
const TITLE_MAX = 520;

function tableWidth(mode: TableViewMode, valueCount: number, titleWidth: number): number {
  const base = COL.drag + COL.done + titleWidth + COL.actions;
  if (mode === 'short') return base + COL.schedShort;
  return base + COL.sched + valueCount * COL.value + COL.etc + COL.priority + COL.add;
}

function SortHeaderCell({
  label,
  width,
  active,
  dir,
  onPress,
  extraStyle,
}: {
  label: string;
  width: number;
  active: boolean;
  dir: 'asc' | 'desc';
  onPress: () => void;
  extraStyle?: any;
}) {
  const styles = useStyles();
  return (
    <Pressable style={[styles.thCell, { width }, extraStyle]} onPress={onPress}>
      <Text style={[styles.thText, active && styles.thTextActive]} numberOfLines={1}>
        {label}
        {active ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
      </Text>
    </Pressable>
  );
}

function StaticHeaderCell({ label, width, extraStyle }: { label: string; width: number; extraStyle?: any }) {
  const styles = useStyles();
  return (
    <View style={[styles.thCell, { width }, extraStyle]}>
      <Text style={styles.thText} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

function TaskTableRow({
  task,
  depth,
  priority,
  hasChildren,
  expanded,
  canReorder,
  isFirst,
  isLast,
  isSubtask,
  categoryStrikethrough,
  viewMode,
  titleWidth,
  valueSettingsToDisplay,
  onToggleExpand,
  onToggleDone,
  onOpenEdit,
  onShowPriority,
  onScheduledPress,
  onPickValue,
  onPickEtc,
  onPriorityAddCommit,
  onRowActions,
  onMove,
}: {
  task: Task;
  depth: number;
  priority: number;
  hasChildren: boolean;
  expanded: boolean;
  canReorder: boolean;
  isFirst: boolean;
  isLast: boolean;
  isSubtask: boolean;
  categoryStrikethrough: boolean;
  viewMode: TableViewMode;
  titleWidth: number;
  valueSettingsToDisplay: ValueSetting[];
  onToggleExpand: () => void;
  onToggleDone: () => void;
  onOpenEdit: () => void;
  onShowPriority: () => void;
  onScheduledPress: () => void;
  onPickValue: (vs: ValueSetting) => void;
  onPickEtc: () => void;
  onPriorityAddCommit: (text: string) => void;
  onRowActions: () => void;
  onMove: () => void;
}) {
  const { t, c } = useTheme();
  const W = webPalette(t);
  const styles = useStyles();
  const [addText, setAddText] = useState(task.priorityAdd != null ? String(task.priorityAdd) : '');
  useEffect(() => {
    setAddText(task.priorityAdd != null ? String(task.priorityAdd) : '');
  }, [task.priorityAdd]);

  const isDone = task.done && categoryStrikethrough;
  const width = tableWidth(viewMode, valueSettingsToDisplay.length, titleWidth);

  return (
    <View
      style={[
        styles.trRow,
        { width },
        isSubtask && styles.trRowSubtask,
        isDone && styles.trRowDone,
      ]}
    >
      <Pressable
        style={[styles.tdCell, { width: COL.drag }]}
        onPress={onMove}
        disabled={!canReorder}
        hitSlop={6}
      >
        <Feather name="menu" size={14} color={canReorder ? W.MUTED : t.lineFaint} />
      </Pressable>

      <View style={[styles.tdCell, { width: COL.done }]}>
        {!task.unlisted && (
          <Pressable style={[styles.checkboxBox, task.done && styles.checkboxBoxOn]} onPress={onToggleDone} hitSlop={8}>
            {task.done && <Feather name="check" size={11} color={t.onGold} />}
          </Pressable>
        )}
      </View>

      <Pressable
        style={[styles.tdCell, { width: viewMode === 'short' ? COL.schedShort : COL.sched }]}
        onPress={onScheduledPress}
      >
        <Text style={[styles.tdMuted, task.scheduled && styles.tdText]} numberOfLines={1}>
          {task.scheduled
            ? viewMode === 'short'
              ? formatScheduledShort(task.scheduled)
              : formatScheduled(task.scheduled)
            : viewMode === 'short'
            ? 'D'
            : 'Not set'}
        </Text>
      </Pressable>

      <Pressable
        style={[styles.tdCell, styles.tdTitleCell, { width: titleWidth, paddingLeft: 4 + depth * 14 }]}
        onPress={onOpenEdit}
      >
        {hasChildren ? (
          <Pressable onPress={onToggleExpand} hitSlop={8} style={styles.expandBtn}>
            <Feather name={expanded ? 'chevron-down' : 'chevron-right'} size={13} color={W.MUTED} />
          </Pressable>
        ) : depth > 0 ? (
          <Feather name="corner-down-right" size={11} color={W.MUTED} style={styles.subtaskIcon} />
        ) : (
          <View style={styles.expandSpacer} />
        )}
        <Text
          style={[styles.tdTitleText, task.done && styles.tdTitleDone]}
          numberOfLines={1}
        >
          {task.title || '(untitled task)'}
        </Text>
      </Pressable>

      {viewMode === 'long' && (
        <>
          {valueSettingsToDisplay.map((vs) => {
            const critId = task.valueSelections?.[vs.id];
            const crit = critId ? vs.criteria.find((c) => c.id === critId) : null;
            return (
              <Pressable key={vs.id} style={[styles.tdCell, { width: COL.value }]} onPress={() => onPickValue(vs)}>
                <Text style={[styles.tdMuted, crit && styles.tdText]} numberOfLines={1}>
                  {crit ? crit.title : '-'}
                </Text>
              </Pressable>
            );
          })}

          <Pressable style={[styles.tdCell, { width: COL.etc }]} onPress={onPickEtc}>
            <Text style={[styles.tdMuted, task.etc && styles.tdText]} numberOfLines={1}>
              {task.etc || '-'}
            </Text>
          </Pressable>

          <Pressable style={[styles.tdCell, { width: COL.priority }]} onPress={onShowPriority}>
            <View
              style={[
                styles.priorityPill,
                priority > 7 ? styles.priorityPillHigh : priority > 4 ? styles.priorityPillMed : styles.priorityPillLow,
              ]}
            >
              <Text style={styles.priorityPillText}>
                {priority.toFixed(0)}
              </Text>
            </View>
          </Pressable>

          <View style={[styles.tdCell, { width: COL.add }]}>
            <TextInput
              style={styles.addInput}
              value={addText}
              onChangeText={setAddText}
              onBlur={() => onPriorityAddCommit(addText)}
              onSubmitEditing={() => onPriorityAddCommit(addText)}
              keyboardType="numbers-and-punctuation"
              placeholder="0"
              placeholderTextColor={c.slate}
            />
          </View>
        </>
      )}

      <Pressable style={[styles.tdCell, { width: COL.actions }]} onPress={onRowActions} hitSlop={8}>
        <Feather name="more-horizontal" size={16} color={W.MUTED} />
      </Pressable>
    </View>
  );
}

// ---------- Row flattening (top-level + expanded children, any depth) -----

interface Row {
  task: Task;
  depth: number;
  siblings: Task[];
  index: number;
}

function flattenVisible(
  topLevel: Task[],
  childrenMap: Map<string, Task[]>,
  expanded: Set<string>,
  childVisible: (t: Task) => boolean,
  depth = 0
): Row[] {
  const out: Row[] = [];
  topLevel.forEach((t, i) => {
    out.push({ task: t, depth, siblings: topLevel, index: i });
    if (expanded.has(t.id)) {
      const kids = (childrenMap.get(t.id) ?? []).filter(childVisible);
      out.push(...flattenVisible(kids, childrenMap, expanded, childVisible, depth + 1));
    }
  });
  return out;
}

type SortKey = 'order' | 'title' | 'priority' | 'scheduled' | 'groupId' | 'sg1Id' | 'sg2Id' | 'etc';

// ---------- Main component --------------------------------------------------

export function KyrsTool({ initialTaskId }: ToolProps) {
  const { t, c } = useTheme();
  const W = webPalette(t);
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  // Global settings (read-only mirror of the web Settings page).
  const [groups, setGroups] = useState<Group[]>([]);
  const [valueSettings, setValueSettings] = useState<ValueSetting[]>([]);
  const [etcIntervals, setEtcIntervals] = useState<ETCInterval[]>([]);
  const [unscheduledBonus, setUnscheduledBonus] = useState(0);

  // Lists + counts
  const [lists, setLists] = useState<TaskListRow[]>([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [taskCounts, setTaskCounts] = useState<Record<string, number>>({});
  const [selectedListId, setSelectedListId] = useState<string>(ALL_ACTIVE_ID);

  // Overview vs. long detail navigation (mirrors web src/app/tasks/page.tsx's
  // 'page' | 'down' | 'out' viewMode toggle + the [listId] full-page route).
  //   'page' → tapping a list in the overview PUSHES into the full-screen
  //            long detail view (screen = 'detail'), with an "‹ OUT" button
  //            to return to the overview — same as web's Link-to-/tasks/[id].
  //   'down' → tapping a list expands its tasks inline, accordion-style,
  //            without leaving the overview — mirrors web's Accordion mode.
  //   'out'  → master/detail in one screen: a horizontal list-picker strip
  //            with the selected list's tasks shown below it — mirrors web's
  //            split-pane OUT mode (adapted to phone width as a picker strip
  //            instead of a fixed left column).
  const [screen, setScreen] = useState<'overview' | 'detail'>('overview');
  const [overviewMode, setOverviewMode] = useState<'page' | 'down' | 'out'>('page');
  const [downExpandedId, setDownExpandedId] = useState<string | null>(null);

  // Tasks for the current selection
  const [currentListTasks, setCurrentListTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('order');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Short/Long view toggle — mirrors web's viewMode (default 'short'): short
  // hides the value columns (Urg/Imp/…), ETC, Pri and Add.
  const [viewMode, setViewMode] = useState<TableViewMode>('short');

  // User-resizable Title column (drag handle in the Title header). Ports web's
  // column resizing, scoped to the one column that actually gets truncated on
  // a phone. `titleWidthRef` always holds the committed width; the pan delta is
  // applied against it. The PanResponder MUST be created once (stable, empty
  // deps) — if it were recreated on every width change it would tear down the
  // in-flight gesture mid-drag, which just makes the handle "wiggle" and snap
  // back. `titleResizing` disables the horizontal table scroll during a drag so
  // the ScrollView can't steal the gesture.
  const [titleWidth, setTitleWidth] = useState<number>(COL.title);
  const [titleResizing, setTitleResizing] = useState(false);
  const titleWidthRef = React.useRef<number>(COL.title);
  const titleResize = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onStartShouldSetPanResponderCapture: () => true,
        onMoveShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponderCapture: () => true,
        onPanResponderGrant: () => setTitleResizing(true),
        onPanResponderMove: (_evt, g) => {
          setTitleWidth(Math.max(TITLE_MIN, Math.min(TITLE_MAX, titleWidthRef.current + g.dx)));
        },
        onPanResponderRelease: (_evt, g) => {
          titleWidthRef.current = Math.max(TITLE_MIN, Math.min(TITLE_MAX, titleWidthRef.current + g.dx));
          setTitleResizing(false);
        },
        onPanResponderTerminate: () => setTitleResizing(false),
      }),
    []
  );
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  // Inline "type new task title..." draft row shown at the bottom of every
  // section (uncategorised Active + each category). Keyed by category name,
  // or UNCATEGORIZED_DRAFT_KEY for the top Active section.
  const [quickAddDrafts, setQuickAddDrafts] = useState<Record<string, string>>({});

  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const [formModal, setFormModal] = useState<{
    task: Task | null;
    presetCategory: string | null;
    presetParentId: string | null;
  } | null>(null);

  // Auto-scheduler: transient status banner (mirrors web's toasts — this app
  // has no toast library, so we reuse the same auto-clearing banner pattern
  // ClioTool uses for journal-entry saves).
  const [status, setStatus] = useState<string | null>(null);
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(null), 4000);
    return () => clearTimeout(t);
  }, [status]);

  // Auto-scheduler: per-list settings modal (toggle/defaults/reschedule).
  const [schedulerModalList, setSchedulerModalList] = useState<TaskListRow | null>(null);

  // Auto-scheduler: verification bell + logs.
  const [autoScheduleLogs, setAutoScheduleLogs] = useState<AutoScheduleLogEntry[]>([]);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [rescheduleLogItem, setRescheduleLogItem] = useState<AutoScheduleLogEntry | null>(null);

  // Trash: inline "Deleted — Undo" bar (auto-dismisses after 5s) + the Trash
  // browser sheet (Restore / Delete Forever), mirroring web's moveToTrash +
  // Cmd+Z undo (see header note above).
  const [undoBar, setUndoBar] = useState<{ trashId: string; title: string } | null>(null);
  useEffect(() => {
    if (!undoBar) return;
    const t = setTimeout(() => setUndoBar(null), 5000);
    return () => clearTimeout(t);
  }, [undoBar]);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashItems, setTrashItems] = useState<TrashItem[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const refreshTrash = useCallback(async () => {
    if (!userId) return;
    setTrashLoading(true);
    try {
      const all = await fetchTrash(supabase, userId, 'trash');
      setTrashItems(all.filter((t) => t.source_component === 'Tasks' && t.item_type === 'task'));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTrashLoading(false);
    }
  }, [userId]);

  const refreshAutoScheduleLogs = useCallback(async () => {
    if (!userId) return;
    setAutoScheduleLogs(await fetchAutoScheduleLogs(userId));
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    void refreshAutoScheduleLogs();
  }, [userId, refreshAutoScheduleLogs]);

  useEffect(() => {
    if (!userId) return;
    void refreshTrash();
  }, [userId, refreshTrash]);

  const topGroups = useMemo(() => groups.filter((g) => g.isActive && !g.parentId), [groups]);
  const activeValueSettings = useMemo(() => valueSettings.filter((v) => v.isActive), [valueSettings]);
  const selectedList = useMemo(() => lists.find((l) => l.id === selectedListId) ?? null, [lists, selectedListId]);
  const isVirtualView = selectedListId === ALL_ACTIVE_ID || selectedListId === COMPLETED_ID;

  // ---------- Loaders ----------

  const loadSettings = useCallback(async () => {
    if (!userId) return;
    const { data, error: err } = await supabase
      .from('user_settings')
      .select('groups, value_settings, etc_intervals, unscheduled_task_priority_bonus')
      .eq('user_id', userId)
      .maybeSingle();
    if (err) {
      setError(err.message);
      return;
    }
    if (data) {
      setGroups(Array.isArray(data.groups) ? data.groups : []);
      setValueSettings(Array.isArray(data.value_settings) ? data.value_settings : []);
      setEtcIntervals(Array.isArray(data.etc_intervals) ? data.etc_intervals : []);
      setUnscheduledBonus(
        typeof data.unscheduled_task_priority_bonus === 'number' ? data.unscheduled_task_priority_bonus : 0
      );
    }
  }, [userId]);

  const loadListsAndCounts = useCallback(async (): Promise<TaskListRow[]> => {
    if (!userId) return [];
    const [listsRes, tasksRes] = await Promise.all([
      supabase
        .from(TASK_LISTS_TABLE)
        .select(
          'id, user_id, name, is_deletable, include_in_all_tasks_view, categories, category_settings, created_at, auto_scheduler_enabled, default_group_id, default_etc, olmps_sector'
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: true }),
      supabase.from(TASKS_TABLE).select('list_id, category, unlisted').eq('user_id', userId).eq('done', false),
    ]);
    if (listsRes.error) {
      setError(listsRes.error.message);
      setListsLoaded(true);
      return [];
    }
    const mapped = (listsRes.data ?? []).map(mapList);
    setLists(mapped);
    if (!tasksRes.error && tasksRes.data) {
      const catMap = new Map(mapped.map((l) => [l.id, l.categorySettings] as const));
      const counts: Record<string, number> = {};
      for (const row of tasksRes.data as any[]) {
        if (row.unlisted) continue;
        if (row.category) {
          const cs = catMap.get(row.list_id) || {};
          if (cs[row.category]?.includeInStats === false) continue;
        }
        counts[row.list_id] = (counts[row.list_id] ?? 0) + 1;
      }
      setTaskCounts(counts);
    }
    setListsLoaded(true);
    return mapped;
  }, [userId]);

  const loadTasks = useCallback(
    async (targetListId: string, listsSnapshot: TaskListRow[]) => {
      if (!userId) return;
      setTasksLoading(true);
      try {
        if (targetListId === ALL_ACTIVE_ID) {
          const included = new Set(listsSnapshot.filter((l) => l.includeInAllTasksView !== false).map((l) => l.id));
          const catMap = new Map(listsSnapshot.map((l) => [l.id, l.categorySettings] as const));
          const { data, error: err } = await supabase
            .from(TASKS_TABLE)
            .select('*')
            .eq('user_id', userId)
            .eq('done', false)
            .order('created_at', { ascending: false });
          if (err) throw err;
          const filtered = (data ?? []).map(mapTask).filter((t) => {
            if (!included.has(t.listId)) return false;
            if (t.tabled && !t.category) return false;
            if (t.category) {
              const cs = catMap.get(t.listId) || {};
              return cs[t.category]?.includeInAllTasks ?? true;
            }
            return true;
          });
          setCurrentListTasks(filtered);
        } else if (targetListId === COMPLETED_ID) {
          const { data, error: err } = await supabase
            .from(TASKS_TABLE)
            .select('*')
            .eq('user_id', userId)
            .eq('done', true)
            .order('updated_at', { ascending: false });
          if (err) throw err;
          setCurrentListTasks((data ?? []).map(mapTask));
        } else {
          const { data, error: err } = await supabase
            .from(TASKS_TABLE)
            .select('*')
            .eq('user_id', userId)
            .eq('list_id', targetListId);
          if (err) throw err;
          setCurrentListTasks((data ?? []).map(mapTask));
        }
        setError(null);
      } catch (e: any) {
        setError(e.message ?? 'Failed to load tasks.');
        setCurrentListTasks([]);
      } finally {
        setTasksLoading(false);
      }
    },
    [userId]
  );

  const refresh = useCallback(async () => {
    // A specific list's tasks are fetched by list_id and don't depend on the
    // lists/counts snapshot, so fetch both in parallel — the task rows no
    // longer wait behind the (potentially large) all-tasks counts query. Only
    // the All Active aggregate needs the fresh lists snapshot first.
    if (selectedListId === ALL_ACTIVE_ID) {
      const fresh = await loadListsAndCounts();
      await loadTasks(selectedListId, fresh);
    } else {
      await Promise.all([loadListsAndCounts(), loadTasks(selectedListId, lists)]);
    }
  }, [loadListsAndCounts, loadTasks, selectedListId, lists]);

  // ---------- Auto-Scheduler triggers ----------
  // Mirrors web's three "re-run the whole list" call sites in
  // src/app/tasks/[listId]/page.tsx: task add, done-toggle, and delete.
  // Always safe to call for any list — scheduleListNow no-ops (returns
  // `{ skipped }`) unless auto_scheduler_enabled is actually true.
  const triggerListReschedule = useCallback(
    async (listId: string) => {
      if (!userId) return;
      const result = await scheduleListNow(userId, listId, groups, valueSettings, unscheduledBonus);
      if ('error' in result) {
        setStatus(`Auto-Scheduler error: ${result.error}`);
      } else if (!('skipped' in result) && (result.scheduled > 0 || result.total > 0)) {
        setStatus(
          `Auto-Scheduler: ${result.scheduled} of ${result.total} task${result.total === 1 ? '' : 's'} scheduled.`
        );
        if (result.notices.length > 0) {
          Alert.alert('Auto-Scheduler Notices', result.notices.join('\n'));
        }
        void refreshAutoScheduleLogs();
      }
      await refresh();
    },
    [userId, groups, valueSettings, unscheduledBonus, refresh, refreshAutoScheduleLogs]
  );

  useEffect(() => {
    if (!userId) return;
    void loadSettings();
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!userId || !listsLoaded) return;
    void loadTasks(selectedListId, lists);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListId]);

  // Deep link from NUMA's "Open in KYRS" action: resolve the task's list,
  // jump straight into that list's detail view, then open its edit form
  // once the list's tasks have loaded and contain it.
  const [pendingFocusTaskId, setPendingFocusTaskId] = useState<string | null>(initialTaskId ?? null);
  useEffect(() => {
    if (!initialTaskId || !userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from(TASKS_TABLE)
        .select('list_id')
        .eq('id', initialTaskId)
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled || !data) return;
      setSelectedListId(data.list_id);
      setScreen('detail');
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTaskId, userId]);

  useEffect(() => {
    if (!pendingFocusTaskId) return;
    const task = currentListTasks.find((t) => t.id === pendingFocusTaskId);
    if (!task) return;
    setFormModal({ task, presetCategory: null, presetParentId: null });
    setPendingFocusTaskId(null);
  }, [pendingFocusTaskId, currentListTasks]);

  // ---------- List mutations ----------

  const addList = async (name: string) => {
    if (!userId) return;
    const now = new Date().toISOString();
    try {
      const { data, error: err } = await supabase
        .from(TASK_LISTS_TABLE)
        .insert({
          user_id: userId,
          name,
          is_deletable: true,
          include_in_all_tasks_view: true,
          created_at: now,
          updated_at: now,
        })
        .select()
        .single();
      if (err) throw err;
      const placeholders = ['New Task 1', 'New Task 2', 'New Task 3'].map((title) => ({
        user_id: userId,
        list_id: data.id,
        title,
        done: false,
        created_at: now,
        updated_at: now,
      }));
      await supabase.from(TASKS_TABLE).insert(placeholders);
      setSelectedListId(data.id);
      await refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const renameList = async (list: TaskListRow, newName: string) => {
    if (newName === list.name) return;
    const { error: err } = await supabase
      .from(TASK_LISTS_TABLE)
      .update({ name: newName, updated_at: new Date().toISOString() })
      .eq('id', list.id)
      .eq('user_id', userId!);
    if (err) setError(err.message);
    else await refresh();
  };

  const toggleIncludeInAllTasks = async (list: TaskListRow) => {
    const { error: err } = await supabase
      .from(TASK_LISTS_TABLE)
      .update({ include_in_all_tasks_view: !list.includeInAllTasksView, updated_at: new Date().toISOString() })
      .eq('id', list.id)
      .eq('user_id', userId!);
    if (err) setError(err.message);
    else await refresh();
  };

  const deleteList = async (list: TaskListRow) => {
    if (!list.isDeletable) {
      Alert.alert('Cannot delete', `"${list.name}" cannot be deleted.`);
      return;
    }
    try {
      await supabase.from(TASKS_TABLE).delete().eq('list_id', list.id).eq('user_id', userId!);
      const { error: err } = await supabase.from(TASK_LISTS_TABLE).delete().eq('id', list.id).eq('user_id', userId!);
      if (err) throw err;
      if (selectedListId === list.id) setSelectedListId(ALL_ACTIVE_ID);
      await refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ---------- Auto-Scheduler: per-list settings ----------
  // Mirrors web's handleSaveListSettings in src/app/tasks/page.tsx, scoped to
  // just the Auto-Scheduler-relevant fields (Visible Groups/Values, Default
  // Priority, and "sector" are cosmetic list chrome — see header EXCLUSIONS).

  /** Sector stamped on tasks created in a list (task_lists.olmps_sector). */
  const sectorForList = (listId: string | null | undefined): Sector | null =>
    lists.find((l) => l.id === listId)?.olmpsSector ?? null;

  const saveAutoSchedulerSettings = async (
    list: TaskListRow,
    nextEnabled: boolean,
    nextGroupId: string | null,
    nextETC: string | null,
    nextSector: Sector | null
  ) => {
    if (!userId) return;
    const wasEnabled = list.autoSchedulerEnabled;
    const prereqsMet = autoSchedulerPrerequisitesMet(nextGroupId, nextETC, groups);
    const finalEnabled = nextEnabled && prereqsMet;

    const { error: err } = await supabase
      .from(TASK_LISTS_TABLE)
      .update({
        auto_scheduler_enabled: finalEnabled,
        default_group_id: nextGroupId,
        default_etc: nextETC,
        olmps_sector: nextSector,
        updated_at: new Date().toISOString(),
      })
      .eq('id', list.id)
      .eq('user_id', userId);
    if (err) {
      setError(err.message);
      return;
    }

    // Sector changed: stamp the list's tasks that have no sector yet.
    if (nextSector && nextSector !== list.olmpsSector) {
      const { error: sErr } = await supabase
        .from(TASKS_TABLE)
        .update({ sector: nextSector })
        .eq('list_id', list.id)
        .eq('user_id', userId)
        .is('sector', null);
      if (sErr) setError(sErr.message);
    }

    if (!finalEnabled && wasEnabled) {
      const cleared = await clearListSchedules(userId, list.id);
      setStatus(cleared > 0 ? `Auto-Scheduler disabled. Cleared ${cleared} scheduled task${cleared === 1 ? '' : 's'}.` : 'Auto-Scheduler disabled.');
    } else if (finalEnabled && !wasEnabled) {
      const result = await scheduleListSubset(userId, list.id, nextGroupId, nextETC, groups, valueSettings, unscheduledBonus);
      if ('error' in result) {
        setStatus(`Auto-Scheduler error: ${result.error}`);
      } else {
        setStatus(`Auto-Scheduler enabled: ${result.scheduled} of ${result.total} task${result.total === 1 ? '' : 's'} scheduled.`);
        if (result.notices.length > 0) Alert.alert('Auto-Scheduler Notices', result.notices.join('\n'));
        void refreshAutoScheduleLogs();
      }
    } else if (nextEnabled && !prereqsMet) {
      Alert.alert(
        'Auto-Scheduler Not Activated',
        'Prerequisites (a Default Group that is Schedule Active with time slots, and a Default ETC) must be met.'
      );
    }

    setSchedulerModalList(null);
    await refresh();
  };

  const runForceReschedule = async (list: TaskListRow) => {
    if (!userId) return;
    setSchedulerModalList(null);
    setStatus('Rescheduling…');
    const result = await forceRescheduleList(userId, list.id, groups, valueSettings, unscheduledBonus);
    if ('error' in result) {
      setStatus(`Reschedule error: ${result.error}`);
    } else if ('skipped' in result) {
      setStatus(result.skipped);
    } else {
      setStatus(`Reschedule complete: ${result.scheduled} of ${result.total} task${result.total === 1 ? '' : 's'} scheduled.`);
      if (result.notices.length > 0) Alert.alert('Auto-Scheduler Notices', result.notices.join('\n'));
      void refreshAutoScheduleLogs();
    }
    await refresh();
  };

  const openListMenu = (list: TaskListRow) => {
    Alert.alert(list.name, undefined, [
      {
        text: 'Rename',
        onPress: () =>
          setPrompt({
            title: 'Rename list',
            initial: list.name,
            placeholder: 'List name',
            onSubmit: (v) => {
              setPrompt(null);
              void renameList(list, v);
            },
          }),
      },
      {
        text: list.includeInAllTasksView ? 'Exclude from All Active Tasks' : 'Include in All Active Tasks',
        onPress: () => void toggleIncludeInAllTasks(list),
      },
      {
        text: `Auto-Scheduler… (${list.autoSchedulerEnabled ? 'On' : 'Off'})`,
        onPress: () => setSchedulerModalList(list),
      },
      {
        text: 'Delete List',
        style: 'destructive',
        onPress: () =>
          Alert.alert('Delete list?', `This removes "${list.name}" and ALL its tasks. This cannot be undone.`, [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Delete', style: 'destructive', onPress: () => void deleteList(list) },
          ]),
      },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // ---------- Category mutations ----------

  const addCategory = async (name: string) => {
    const list = selectedList;
    if (!list) return;
    if (list.categories.includes(name)) {
      Alert.alert('Category exists', `"${name}" already exists.`);
      return;
    }
    const { error: err } = await supabase
      .from(TASK_LISTS_TABLE)
      .update({ categories: [...list.categories, name], updated_at: new Date().toISOString() })
      .eq('id', list.id)
      .eq('user_id', userId!);
    if (err) setError(err.message);
    else await refresh();
  };

  const renameCategory = async (oldName: string, newName: string) => {
    const list = selectedList;
    if (!list || newName === oldName) return;
    if (list.categories.includes(newName)) {
      Alert.alert('Category exists', `"${newName}" already exists.`);
      return;
    }
    const now = new Date().toISOString();
    const newCategories = list.categories.map((c) => (c === oldName ? newName : c));
    const newSettings = { ...list.categorySettings };
    if (newSettings[oldName]) {
      newSettings[newName] = newSettings[oldName];
      delete newSettings[oldName];
    }
    try {
      const { error: err } = await supabase
        .from(TASK_LISTS_TABLE)
        .update({ categories: newCategories, category_settings: newSettings, updated_at: now })
        .eq('id', list.id)
        .eq('user_id', userId!);
      if (err) throw err;
      const affected = currentListTasks.filter((t) => t.category === oldName);
      await Promise.all(
        affected.map((t) =>
          supabase.from(TASKS_TABLE).update({ category: newName, updated_at: now }).eq('id', t.id).eq('user_id', userId!)
        )
      );
      await refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const deleteCategory = async (name: string) => {
    const list = selectedList;
    if (!list) return;
    const now = new Date().toISOString();
    try {
      const affected = currentListTasks.filter((t) => t.category === name);
      await Promise.all(
        affected.map((t) =>
          supabase
            .from(TASKS_TABLE)
            .update({ category: null, tabled: false, updated_at: now })
            .eq('id', t.id)
            .eq('user_id', userId!)
        )
      );
      const { error: err } = await supabase
        .from(TASK_LISTS_TABLE)
        .update({ categories: list.categories.filter((c) => c !== name), updated_at: now })
        .eq('id', list.id)
        .eq('user_id', userId!);
      if (err) throw err;
      await refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleCategoryStrikethrough = async (name: string) => {
    const list = selectedList;
    if (!list) return;
    const current = list.categorySettings[name]?.strikethrough ?? false;
    const newSettings = { ...list.categorySettings, [name]: { ...list.categorySettings[name], strikethrough: !current } };
    const { error: err } = await supabase
      .from(TASK_LISTS_TABLE)
      .update({ category_settings: newSettings, updated_at: new Date().toISOString() })
      .eq('id', list.id)
      .eq('user_id', userId!);
    if (err) setError(err.message);
    else await refresh();
  };

  const openCategoryMenu = (name: string) => {
    const list = selectedList;
    if (!list) return;
    const strike = list.categorySettings[name]?.strikethrough ?? false;
    Alert.alert(name, undefined, [
      {
        text: 'Rename Category',
        onPress: () =>
          setPrompt({
            title: 'Rename category',
            initial: name,
            placeholder: 'Category name',
            onSubmit: (v) => {
              setPrompt(null);
              void renameCategory(name, v);
            },
          }),
      },
      {
        text: strike ? 'Hide completed here' : 'Show completed here',
        onPress: () => void toggleCategoryStrikethrough(name),
      },
      {
        text: 'Delete Category',
        style: 'destructive',
        onPress: () =>
          Alert.alert('Delete category?', `Tasks in "${name}" move back to Active.`, [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Delete', style: 'destructive', onPress: () => void deleteCategory(name) },
          ]),
      },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // ---------- Task mutations ----------

  const toggleDone = async (task: Task) => {
    if (!userId) return;
    const nextDone = !task.done;
    const now = new Date().toISOString();
    const patch: Record<string, any> = { done: nextDone, updated_at: now };
    if (nextDone) {
      // Keep `scheduled`: lib/flo.ts counts today's completed scheduled tasks
      // as Charge blocks. Every open-task reader already filters on done.
      patch.scheduled_manual = false;
    }
    // Optimistic: flip the checkbox instantly so the tap feels responsive and
    // (in the Active/virtual views that hide done tasks) the row drops out
    // immediately, rather than waiting on the reschedule round-trip.
    setCurrentListTasks((prev) =>
      isVirtualView && nextDone
        ? prev.filter((t) => t.id !== task.id)
        : prev.map((t) =>
            t.id === task.id
              ? { ...t, done: nextDone, ...(nextDone ? { scheduledManual: false } : {}) }
              : t
          )
    );
    const { error: err } = await supabase.from(TASKS_TABLE).update(patch).eq('id', task.id).eq('user_id', userId);
    if (err) {
      setError(err.message);
      await refresh(); // roll back optimistic state from source of truth
      return;
    }
    // Web re-runs the list's full auto-scheduler on BOTH done and undone
    // (freeing a slot, or re-admitting the task into the schedulable pool).
    await triggerListReschedule(task.listId);
  };

  /** Generic single/multi-field patch used by the table's inline cell editors
   *  (Group/SG1/SG2/ETC/Priority-Add/Category/Unlisted/Scheduled) — mirrors
   *  web's handleTaskFieldChange (src/app/tasks/[listId]/page.tsx), minus the
   *  reschedule trigger which only fires for the fields that actually affect
   *  scheduling eligibility (matches web: only add/done/delete reschedule). */
  const updateTaskField = async (task: Task, patch: Record<string, any>) => {
    const { error: err } = await supabase
      .from(TASKS_TABLE)
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', task.id)
      .eq('user_id', userId!);
    if (err) {
      setError(err.message);
      return;
    }
    await refresh();
  };

  const deleteTask = async (task: Task) => {
    if (!userId) return;
    try {
      // Snapshot the raw row into trash before removing it — mirrors web's
      // handleDeleteTask (src/app/tasks/[listId]/page.tsx): fetch the full
      // row, moveToTrash it, THEN delete. Byte-compatible `trash` row so
      // web's Settings → Trash page can restore anything deleted here.
      const { data: taskRow, error: fetchErr } = await supabase
        .from(TASKS_TABLE)
        .select('*')
        .eq('id', task.id)
        .single();
      if (fetchErr) throw fetchErr;

      let trashId: string | null = null;
      if (taskRow) {
        const { data: inserted, error: trashErr } = await supabase
          .from('trash')
          .insert({
            user_id: userId,
            source_component: 'Tasks',
            item_type: 'task',
            original_table: TASKS_TABLE,
            original_id: task.id,
            item_title: taskRow.title || taskRow.name || 'Untitled Task',
            item_data: taskRow,
            kind: 'trash',
          })
          .select('id')
          .single();
        // CRITICAL (matches web's moveToTrash comment): abort the delete if
        // the trash snapshot failed to write — otherwise the task vanishes
        // unrecoverably.
        if (trashErr) throw trashErr;
        trashId = inserted?.id ?? null;
      }

      const now = new Date().toISOString();
      const children = currentListTasks.filter((t) => t.parentTaskId === task.id);
      await Promise.all(
        children.map((c) =>
          supabase.from(TASKS_TABLE).update({ parent_task_id: null, updated_at: now }).eq('id', c.id).eq('user_id', userId)
        )
      );
      const { error: err } = await supabase.from(TASKS_TABLE).delete().eq('id', task.id).eq('user_id', userId);
      if (err) throw err;

      setCurrentListTasks((prev) => prev.filter((t) => t.id !== task.id));
      if (trashId) {
        setUndoBar({ trashId, title: task.title || 'Untitled task' });
        void refreshTrash();
      }

      // Deleting a task can free up a slot for the rest of the list.
      await triggerListReschedule(task.listId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const confirmDeleteTask = (task: Task) => {
    Alert.alert('Delete task?', task.title || 'Untitled task', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => void deleteTask(task) },
    ]);
  };

  /** Restore a trashed row exactly as web's restoreFromTrash does: re-insert
   *  the snapshotted row into its original table, then remove the trash
   *  entry. Used by both the Undo bar and the Trash sheet's Restore button. */
  const restoreTrashItem = async (item: TrashItem) => {
    try {
      await restoreFromTrash(supabase, item.id);
      setTrashItems((prev) => prev.filter((t) => t.id !== item.id));
      if (undoBar?.trashId === item.id) setUndoBar(null);
      setStatus(`"${item.item_title}" restored.`);
      await refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const permanentDeleteTrashItem = (item: TrashItem) => {
    Alert.alert('Delete forever?', `"${item.item_title}" cannot be recovered after this.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete Forever',
        style: 'destructive',
        onPress: () =>
          void permanentDelete(supabase, item.id)
            .then(() => setTrashItems((prev) => prev.filter((t) => t.id !== item.id)))
            .catch((e: any) => setError(e.message)),
      },
    ]);
  };

  const emptyTrash = () => {
    if (trashItems.length === 0) return;
    Alert.alert('Empty trash?', `Permanently delete all ${trashItems.length} trashed task(s). This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Empty Trash',
        style: 'destructive',
        onPress: () =>
          void Promise.all(trashItems.map((item) => permanentDelete(supabase, item.id)))
            .then(() => setTrashItems([]))
            .catch((e: any) => setError(e.message)),
      },
    ]);
  };

  const saveTaskForm = async (value: TaskFormValue, editingTask: Task | null) => {
    if (!userId) return;
    const now = new Date().toISOString();
    const scheduled = value.scheduled && value.scheduled.trim() !== '' ? value.scheduled.trim() : null;
    try {
      if (editingTask) {
        const patch: Record<string, any> = {
          title: value.title,
          done: value.done,
          group_id: value.groupId,
          sg1_id: value.groupId ? value.sg1Id : null,
          sg2_id: value.groupId && value.sg1Id ? value.sg2Id : null,
          value_selections: value.valueSelections,
          etc: value.etc,
          scheduled,
          scheduled_manual: scheduled !== null,
          priority_add: value.priorityAdd,
          notes: value.notes,
          category: value.category,
          tabled: !!value.category,
          unlisted: value.unlisted,
          parent_task_id: value.parentTaskId,
          updated_at: now,
        };
        if (value.moveToListId && value.moveToListId !== editingTask.listId) {
          patch.list_id = value.moveToListId;
          const movedSector = sectorForList(value.moveToListId);
          if (movedSector) patch.sector = movedSector;
        }
        const { error: err } = await supabase
          .from(TASKS_TABLE)
          .update(patch)
          .eq('id', editingTask.id)
          .eq('user_id', userId);
        if (err) throw err;
        if (value.category !== editingTask.category) {
          const children = currentListTasks.filter((t) => t.parentTaskId === editingTask.id);
          await Promise.all(
            children.map((c) =>
              supabase
                .from(TASKS_TABLE)
                .update({ category: value.category, tabled: !!value.category, updated_at: now })
                .eq('id', c.id)
                .eq('user_id', userId)
            )
          );
        }
        if (patch.list_id) {
          await supabase
            .from(TASKS_TABLE)
            .update({ list_id: patch.list_id, ...(patch.sector ? { sector: patch.sector } : {}), updated_at: now })
            .eq('parent_task_id', editingTask.id)
            .eq('user_id', userId);
        }
      } else {
        if (isVirtualView) return;
        const siblings = value.parentTaskId
          ? currentListTasks.filter((t) => t.parentTaskId === value.parentTaskId)
          : currentListTasks.filter((t) => t.category === value.category && !t.parentTaskId);
        const minOrder = siblings.reduce((m, t) => Math.min(m, t.order ?? 0), 0);
        const payload = {
          user_id: userId,
          list_id: selectedListId,
          title: value.title,
          done: false,
          group_id: value.groupId,
          sg1_id: value.groupId ? value.sg1Id : null,
          sg2_id: value.groupId && value.sg1Id ? value.sg2Id : null,
          value_selections: value.valueSelections,
          etc: value.etc,
          scheduled,
          scheduled_manual: scheduled !== null,
          priority_add: value.priorityAdd,
          notes: value.notes,
          category: value.category,
          tabled: !!value.category,
          unlisted: value.unlisted,
          parent_task_id: value.parentTaskId,
          sector: sectorForList(selectedListId),
          order: minOrder - 1,
          created_at: now,
          updated_at: now,
        };
        const { error: err } = await supabase.from(TASKS_TABLE).insert(payload);
        if (err) throw err;
      }
      setFormModal(null);
      if (!editingTask) {
        // Matches web's handleAddTask: only NEW tasks trigger the list's
        // auto-scheduler; editing an existing task's fields does not.
        await triggerListReschedule(selectedListId);
      } else {
        await refresh();
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  /** Bottom-of-section "Type new task title..." draft row. Mirrors the
   *  new-task branch of saveTaskForm with just the fields a bare title needs,
   *  so a task list is never more than one tap + enter away — no modal. */
  const quickAddTask = async (category: string | null, title: string) => {
    const trimmed = title.trim();
    const draftKey = category ?? UNCATEGORIZED_DRAFT_KEY;
    if (!userId || !trimmed || isVirtualView) return;
    setQuickAddDrafts((prev) => ({ ...prev, [draftKey]: '' }));
    try {
      const now = new Date().toISOString();
      const siblings = currentListTasks.filter((t) => t.category === category && !t.parentTaskId);
      const minOrder = siblings.reduce((m, t) => Math.min(m, t.order ?? 0), 0);
      const payload = {
        user_id: userId,
        list_id: selectedListId,
        title: trimmed,
        done: false,
        category,
        tabled: !!category,
        unlisted: false,
        parent_task_id: null,
        sector: sectorForList(selectedListId),
        order: minOrder - 1,
        created_at: now,
        updated_at: now,
      };
      const { error: err } = await supabase.from(TASKS_TABLE).insert(payload);
      if (err) throw err;
      await triggerListReschedule(selectedListId);
    } catch (e: any) {
      setError(e.message);
      setQuickAddDrafts((prev) => ({ ...prev, [draftKey]: trimmed }));
    }
  };

  const reorderTask = async (task: Task, siblings: Task[], direction: 1 | -1) => {
    const idx = siblings.findIndex((t) => t.id === task.id);
    const otherIdx = idx + direction;
    if (idx < 0 || otherIdx < 0 || otherIdx >= siblings.length) return;
    const reordered = siblings.slice();
    const tmp = reordered[idx];
    reordered[idx] = reordered[otherIdx];
    reordered[otherIdx] = tmp;
    const now = new Date().toISOString();
    try {
      await Promise.all(
        reordered.map((t, i) =>
          supabase.from(TASKS_TABLE).update({ order: i, updated_at: now }).eq('id', t.id).eq('user_id', userId!)
        )
      );
      await refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ---------- Derived: sorted tasks + section rows ----------

  const sortedTasks = useMemo(() => {
    const arr = currentListTasks.slice();
    if (sortKey === 'order') {
      if (selectedListId === COMPLETED_ID) {
        arr.sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime());
      } else {
        arr.sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
      }
      return arr;
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (sortKey === 'priority') {
        return (calcPriority(a, groups, valueSettings, unscheduledBonus) - calcPriority(b, groups, valueSettings, unscheduledBonus)) * dir;
      }
      if (sortKey === 'scheduled') {
        const ta = parseScheduledMillis(a.scheduled);
        const tb = parseScheduledMillis(b.scheduled);
        if (ta === null && tb === null) return 0;
        if (ta === null) return dir;
        if (tb === null) return -dir;
        return (ta - tb) * dir;
      }
      if (sortKey === 'title') {
        return a.title.localeCompare(b.title) * dir;
      }
      // groupId / sg1Id / sg2Id / etc — matches web's generic handleSort
      // branch: raw stored value string-compared (id/label, not a resolved
      // title), '' sorts first ascending.
      const av = (a[sortKey] as string | null) ?? '';
      const bv = (b[sortKey] as string | null) ?? '';
      return av.localeCompare(bv) * dir;
    });
    return arr;
  }, [currentListTasks, sortKey, sortDir, groups, valueSettings, unscheduledBonus, selectedListId]);

  const allIds = useMemo(() => new Set(currentListTasks.map((t) => t.id)), [currentListTasks]);

  const childrenMap = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const t of sortedTasks) {
      if (t.parentTaskId) {
        const arr = map.get(t.parentTaskId) ?? [];
        arr.push(t);
        map.set(t.parentTaskId, arr);
      }
    }
    return map;
  }, [sortedTasks]);

  const toggleExpand = (taskId: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const priorityOf = (t: Task) => calcPriority(t, groups, valueSettings, unscheduledBonus);

  const showPriority = (t: Task) => {
    const parts = explainPriority(t, groups, valueSettings, unscheduledBonus);
    const total = priorityOf(t);
    const body =
      parts.length > 0
        ? parts.map((p) => `${p.label}: ${p.points >= 0 ? '+' : ''}${p.points}`).join('\n')
        : 'No contributing factors.';
    Alert.alert(`Priority score: ${total}`, body);
  };

  const openAddTask = (presetCategory: string | null, presetParentId: string | null = null) => {
    setFormModal({ task: null, presetCategory, presetParentId });
  };
  const openEditTask = (task: Task) => {
    setFormModal({ task, presetCategory: null, presetParentId: null });
  };

  const canReorder = sortKey === 'order' && !isVirtualView;

  // ---------- Table cell editors ----------
  // Web edits these cells with an inline shadcn <Select> (mouse dropdown).
  // Native has no equivalent + no new deps are allowed, so each opens a
  // native Alert action-sheet with the same option set — same field, same
  // persistence (updateTaskField), touch-appropriate control.

  const openValuePicker = (task: Task, vs: ValueSetting) => {
    const currentId = task.valueSelections?.[vs.id];
    Alert.alert(
      vs.name,
      undefined,
      [
        {
          text: currentId ? 'None' : '✓ None',
          onPress: () => {
            const next = { ...task.valueSelections };
            delete next[vs.id];
            void updateTaskField(task, { value_selections: next });
          },
        },
        ...vs.criteria.map((c) => ({
          text: currentId === c.id ? `✓ ${c.title} (${c.score})` : `${c.title} (${c.score})`,
          onPress: () => void updateTaskField(task, { value_selections: { ...task.valueSelections, [vs.id]: c.id } }),
        })),
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  const openEtcPicker = (task: Task) => {
    Alert.alert(
      'ETC (duration)',
      undefined,
      [
        { text: task.etc ? 'None' : '✓ None', onPress: () => void updateTaskField(task, { etc: null }) },
        ...etcIntervals.map((e) => ({
          text: task.etc === e.label ? `✓ ${e.label}` : e.label,
          onPress: () => void updateTaskField(task, { etc: e.label }),
        })),
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  const openScheduledPicker = (task: Task) => {
    Alert.alert(
      'Scheduled',
      undefined,
      [
        { text: 'Today', onPress: () => void updateTaskField(task, { scheduled: dateStr(0), scheduled_manual: true }) },
        { text: 'Tomorrow', onPress: () => void updateTaskField(task, { scheduled: dateStr(1), scheduled_manual: true }) },
        {
          text: 'Custom…',
          onPress: () =>
            setPrompt({
              title: 'Scheduled (yyyy-MM-dd HH:mm)',
              initial: task.scheduled ?? '',
              placeholder: 'yyyy-MM-dd HH:mm',
              onSubmit: (v) => {
                setPrompt(null);
                void updateTaskField(task, { scheduled: v, scheduled_manual: true });
              },
            }),
        },
        { text: 'Clear', style: 'destructive', onPress: () => void updateTaskField(task, { scheduled: null, scheduled_manual: false }) },
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  const openTaskCategoryPicker = (task: Task) => {
    Alert.alert(
      'Category / section',
      undefined,
      [
        {
          text: task.category == null ? '✓ None (Active)' : 'None (Active)',
          onPress: () => void updateTaskField(task, { category: null, tabled: false }),
        },
        ...formModalCategories.map((cat) => ({
          text: task.category === cat ? `✓ ${cat}` : cat,
          onPress: () => void updateTaskField(task, { category: cat, tabled: true }),
        })),
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  const openMoveListPicker = (task: Task) => {
    const others = lists.filter((l) => l.id !== task.listId);
    if (others.length === 0) return;
    Alert.alert(
      'Move to list',
      undefined,
      [
        ...others.map((l) => ({ text: l.name, onPress: () => void updateTaskField(task, { list_id: l.id }) })),
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  const openParentPicker = (task: Task) => {
    const eligible = currentListTasks.filter((t) => t.id !== task.id && t.parentTaskId !== task.id && !t.parentTaskId && !t.done);
    Alert.alert(
      'Subtask',
      undefined,
      [
        { text: 'Add subtask under this task', onPress: () => openAddTask(task.category, task.id) },
        ...(task.parentTaskId ? [{ text: 'Remove from parent', onPress: () => void updateTaskField(task, { parent_task_id: null }) }] : []),
        ...eligible.map((t) => ({
          text: `Make subtask of "${t.title || '(untitled)'}"`,
          onPress: () => void updateTaskField(task, { parent_task_id: t.id }),
        })),
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  const openRowActionsMenu = (task: Task) => {
    Alert.alert(
      task.title || 'Untitled task',
      undefined,
      [
        { text: 'Edit', onPress: () => openEditTask(task) },
        { text: 'Subtask…', onPress: () => openParentPicker(task) },
        { text: 'Move to list…', onPress: () => openMoveListPicker(task) },
        ...(canAddTaskToList ? [{ text: 'Category…', onPress: () => openTaskCategoryPicker(task) }] : []),
        {
          text: task.unlisted ? 'Remove Unlisted' : 'Unlist',
          onPress: () => void updateTaskField(task, { unlisted: !task.unlisted }),
        },
        { text: 'Delete', style: 'destructive' as const, onPress: () => confirmDeleteTask(task) },
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  const moveTask = (task: Task, siblings: Task[]) => {
    const idx = siblings.findIndex((t) => t.id === task.id);
    const isFirst = idx <= 0;
    const isLast = idx < 0 || idx >= siblings.length - 1;
    if (isFirst && isLast) return;
    Alert.alert(
      task.title || 'Untitled task',
      undefined,
      [
        ...(!isFirst ? [{ text: '▲ Move Up', onPress: () => void reorderTask(task, siblings, -1) }] : []),
        ...(!isLast ? [{ text: '▼ Move Down', onPress: () => void reorderTask(task, siblings, 1) }] : []),
        { text: 'Cancel', style: 'cancel' as const },
      ]
    );
  };

  // ---------- Render helpers for a section ----------
  // canAddTaskToList mirrors web's same-named const: false for the two
  // virtual views (All Active / Completed), which are read-only aggregates.
  const canAddTaskToList = !isVirtualView;

  /** Always-present blank "Type new task title..." row at the bottom of every
   *  section (uncategorised Active + each category) — tapping it and typing
   *  a title creates the task on submit, then clears so the row is ready for
   *  the next one. Replaces the old "+ Add to <Category>" modal-opening link. */
  const renderQuickAddRow = (category: string | null) => {
    const draftKey = category ?? UNCATEGORIZED_DRAFT_KEY;
    const value = quickAddDrafts[draftKey] ?? '';
    return (
      <View style={styles.quickAddRow}>
        <Feather name="plus" size={12} color={W.MUTED} />
        <TextInput
          style={styles.quickAddInput}
          placeholder="Type new task title..."
          placeholderTextColor={W.MUTED}
          value={value}
          onChangeText={(text) => setQuickAddDrafts((prev) => ({ ...prev, [draftKey]: text }))}
          onSubmitEditing={() => void quickAddTask(category, value)}
          blurOnSubmit={false}
          returnKeyType="done"
        />
      </View>
    );
  };

  const renderRows = (rows: Row[], categoryStrikethrough: boolean) =>
    rows.map(({ task, depth, siblings, index }) => (
      <TaskTableRow
        key={task.id}
        task={task}
        depth={depth}
        priority={priorityOf(task)}
        hasChildren={(childrenMap.get(task.id) ?? []).length > 0}
        expanded={expandedParents.has(task.id)}
        canReorder={canReorder}
        isFirst={index === 0}
        isLast={index === siblings.length - 1}
        isSubtask={depth > 0}
        categoryStrikethrough={categoryStrikethrough}
        viewMode={viewMode}
        titleWidth={titleWidth}
        valueSettingsToDisplay={activeValueSettings}
        onToggleExpand={() => toggleExpand(task.id)}
        onToggleDone={() => void toggleDone(task)}
        onOpenEdit={() => openEditTask(task)}
        onShowPriority={() => showPriority(task)}
        onScheduledPress={() => openScheduledPicker(task)}
        onPickValue={(vs) => openValuePicker(task, vs)}
        onPickEtc={() => openEtcPicker(task)}
        onPriorityAddCommit={(text) => {
          const trimmed = text.trim();
          const parsed = trimmed === '' ? null : Number(trimmed.replace(/[^0-9.-]/g, ''));
          void updateTaskField(task, { priority_add: parsed !== null && !Number.isNaN(parsed) ? parsed : null });
        }}
        onRowActions={() => openRowActionsMenu(task)}
        onMove={() => moveTask(task, siblings)}
      />
    ));

  const renderTableHeaderRow = () => {
    const width = tableWidth(viewMode, activeValueSettings.length, titleWidth);
    const sortHeader = (key: SortKey, label: string, cellWidth: number, extraStyle?: any) => (
      <SortHeaderCell
        key={key}
        label={label}
        width={cellWidth}
        active={sortKey === key}
        dir={sortDir}
        extraStyle={extraStyle}
        onPress={() => {
          if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortKey(key);
            setSortDir('asc');
          }
        }}
      />
    );
    return (
      <View style={[styles.trHeader, { width }]}>
        <View style={[styles.thCell, { width: COL.drag }]} />
        <StaticHeaderCell label="Done" width={COL.done} />
        {viewMode === 'short'
          ? sortHeader('scheduled', 'D', COL.schedShort)
          : sortHeader('scheduled', 'Date', COL.sched)}
        {/* Title header: tap the label to sort, drag the right-edge handle to
            resize the whole column so long titles are readable. */}
        <View style={[styles.thCell, styles.thTitleCell, { width: titleWidth }]}>
          <Pressable
            style={styles.thTitleLabelHit}
            onPress={() => {
              if (sortKey === 'title') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
              else {
                setSortKey('title');
                setSortDir('asc');
              }
            }}
          >
            <Text style={[styles.thText, sortKey === 'title' && styles.thTextActive]} numberOfLines={1}>
              Title{sortKey === 'title' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
            </Text>
          </Pressable>
          <View style={styles.thResizeHandle} hitSlop={10} {...titleResize.panHandlers}>
            <View style={styles.thResizeGrip} />
          </View>
        </View>
        {viewMode === 'long' && (
          <>
            {activeValueSettings.map((vs) => (
              <StaticHeaderCell key={vs.id} label={vs.name.substring(0, 3)} width={COL.value} />
            ))}
            {sortHeader('etc', 'ETC', COL.etc)}
            {sortHeader('priority', 'Pri', COL.priority)}
            <StaticHeaderCell label="Add" width={COL.add} />
          </>
        )}
        <StaticHeaderCell label="•••" width={COL.actions} />
      </View>
    );
  };

  // ---------- Form modal option lists ----------

  const formModalCategories = selectedList?.categories ?? ['Tabled'];
  const formModalParentOptions = useMemo(() => {
    if (!formModal || isVirtualView) return [];
    return currentListTasks
      .filter((t) => t.id !== formModal.task?.id && t.parentTaskId !== formModal.task?.id)
      .map((t) => ({ id: t.id, label: t.title || '(untitled task)' }));
  }, [currentListTasks, formModal, isVirtualView]);
  const formModalOtherLists = useMemo(
    () => lists.filter((l) => l.id !== selectedListId).map((l) => ({ id: l.id, label: l.name })),
    [lists, selectedListId]
  );

  // ---------- Loading / auth states ----------

  if (!userId) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyText}>Please log in to manage your task lists.</Text>
      </View>
    );
  }

  const chipEntries: Array<{ id: string; name: string; virtual: boolean }> = [
    { id: ALL_ACTIVE_ID, name: 'All Active Tasks', virtual: true },
    ...lists.map((l) => ({ id: l.id, name: l.name, virtual: false })),
    { id: COMPLETED_ID, name: 'Completed', virtual: true },
  ];

  // ---- Shared header icon cluster (trash + auto-schedule verification bell)
  const renderHeaderIcons = () => (
    <View style={styles.headerIconsRow}>
      <Pressable
        style={styles.trashButton}
        onPress={() => {
          setTrashOpen(true);
          void refreshTrash();
        }}
        accessibilityLabel="Trash"
      >
        <Text style={styles.trashIcon}>🗑️</Text>
        {trashItems.length > 0 && (
          <View style={styles.bellBadge}>
            <Text style={styles.bellBadgeText}>{trashItems.length}</Text>
          </View>
        )}
      </Pressable>
      <Pressable style={styles.bellButton} onPress={() => setVerificationOpen(true)} accessibilityLabel="Auto-schedule verification">
        <Text style={styles.bellIcon}>🔔</Text>
        {autoScheduleLogs.length > 0 && (
          <View style={styles.bellBadge}>
            <Text style={styles.bellBadgeText}>{autoScheduleLogs.length}</Text>
          </View>
        )}
      </Pressable>
    </View>
  );

  // ---- Toolbar (sort chips + Task/Category add) for whichever list is
  // currently loaded (`selectedListId`) — shared by the OUT strip, the DOWN
  // accordion body, and the full-screen long detail view.
  // ---- Header/toolbar above the table — flat 1:1 port of the web detail
  // page's action row (src/app/tasks/[listId]/page.tsx: "+ Add Task" /
  // "Expand All"/"Collapse All" outline buttons). Sorting itself moved to
  // the table's own column headers (tap-to-sort), matching web's
  // click-a-column-header-to-sort exactly — the sort chip row above the
  // table is gone (that was the mobile-only affordance).
  const renderToolbar = () => {
    const hasAnyChildren = childrenMap.size > 0;
    return (
      <View style={styles.toolbar}>
        <View style={styles.toolbarButtons}>
          {/* View toggle shows everywhere, incl. All Active / Completed. */}
          <Pressable
            style={styles.outlineButton}
            onPress={() => setViewMode((m) => (m === 'short' ? 'long' : 'short'))}
          >
            <Feather
              name={viewMode === 'short' ? 'maximize-2' : 'minimize-2'}
              size={14}
              color={W.PRIMARY}
              style={styles.outlineButtonIcon}
            />
            <Text style={styles.outlineButtonText}>{viewMode === 'short' ? 'Long View' : 'Short View'}</Text>
          </Pressable>
          {!isVirtualView && (
            <Pressable style={styles.outlineButton} onPress={() => openAddTask(null)}>
              <Feather name="plus-circle" size={14} color={W.PRIMARY} style={styles.outlineButtonIcon} />
              <Text style={styles.outlineButtonText}>Add Task</Text>
            </Pressable>
          )}
          {!isVirtualView && hasAnyChildren && (
            <Pressable
              style={styles.outlineButton}
              onPress={() =>
                setExpandedParents((prev) => (prev.size > 0 ? new Set() : new Set(childrenMap.keys())))
              }
            >
              <Feather
                name={expandedParents.size > 0 ? 'chevron-down' : 'chevron-right'}
                size={14}
                color={W.PRIMARY}
                style={styles.outlineButtonIcon}
              />
              <Text style={styles.outlineButtonText}>{expandedParents.size > 0 ? 'Collapse All' : 'Expand All'}</Text>
            </Pressable>
          )}
          {!isVirtualView && (
            <Pressable
              style={styles.outlineButton}
              onPress={() =>
                setPrompt({
                  title: 'New category',
                  placeholder: 'Category name',
                  onSubmit: (v) => {
                    setPrompt(null);
                    void addCategory(v);
                  },
                })
              }
            >
              <Feather name="folder-plus" size={14} color={W.PRIMARY} style={styles.outlineButtonIcon} />
              <Text style={styles.outlineButtonText}>Add Category</Text>
            </Pressable>
          )}
        </View>
      </View>
    );
  };

  // ---- Task table for whichever list is currently loaded (`selectedListId`)
  // — same three consumers as renderToolbar above. 1:1 structural port of
  // web's flat <Table>: each section (uncategorised "Active" list, then one
  // per category) is its own Card with a header row + horizontally
  // scrollable table (renderTableHeaderRow + TaskTableRow), exactly
  // mirroring web's per-section <Accordion><Table>{renderTableHeader()}
  // {rows}</Table></Accordion> structure.
  const renderTaskBody = () => (
    <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
      {isVirtualView ? (
        (() => {
          const topLevel = sortedTasks.filter((t) => !t.parentTaskId || !allIds.has(t.parentTaskId));
          const rows = flattenVisible(topLevel, childrenMap, expandedParents, () => true);
          return (
            <GlassCard style={styles.sectionCard}>
              {rows.length === 0 ? (
                <Text style={styles.emptyText}>
                  {selectedListId === COMPLETED_ID ? 'No completed tasks yet.' : 'No active tasks.'}
                </Text>
              ) : (
                <ScrollView horizontal showsHorizontalScrollIndicator scrollEnabled={!titleResizing}>
                  <View>
                    {renderTableHeaderRow()}
                    {renderRows(rows, true)}
                  </View>
                </ScrollView>
              )}
            </GlassCard>
          );
        })()
      ) : (
        <>
          <GlassCard style={styles.sectionCard}>
            <View style={styles.sectionHeaderRow}>
              <Text style={styles.sectionHeaderText}>{selectedList?.name ?? 'Active'}</Text>
            </View>
            {(() => {
              const topLevel = sortedTasks.filter(
                (t) => !t.category && !t.done && (!t.parentTaskId || !allIds.has(t.parentTaskId))
              );
              const rows = flattenVisible(topLevel, childrenMap, expandedParents, (c) => !c.done);
              return rows.length === 0 ? (
                <Text style={styles.emptySection}>Nothing here yet.</Text>
              ) : (
                <ScrollView horizontal showsHorizontalScrollIndicator scrollEnabled={!titleResizing}>
                  <View>
                    {renderTableHeaderRow()}
                    {renderRows(rows, false)}
                  </View>
                </ScrollView>
              );
            })()}
            {renderQuickAddRow(null)}
          </GlassCard>

          {formModalCategories.map((cat) => {
            const strike = selectedList?.categorySettings[cat]?.strikethrough ?? false;
            const catAll = sortedTasks.filter((t) => t.category === cat && (strike || !t.done));
            const topLevel = catAll.filter((t) => !t.parentTaskId || !allIds.has(t.parentTaskId));
            const rows = flattenVisible(topLevel, childrenMap, expandedParents, (c) => strike || !c.done);
            return (
              <GlassCard key={cat} style={styles.sectionCard}>
                <Pressable style={styles.sectionHeaderRow} onPress={() => openCategoryMenu(cat)}>
                  <Text style={styles.sectionHeaderText}>{cat}</Text>
                  <Text style={styles.sectionCountBadge}>{topLevel.length}</Text>
                  {strike && <Feather name="minus" size={12} color={W.MUTED} style={{ marginLeft: 4 }} />}
                  <View style={{ flex: 1 }} />
                  <Feather name="more-horizontal" size={16} color={W.MUTED} />
                </Pressable>
                {rows.length === 0 ? (
                  <Text style={styles.emptySection}>No tasks in this category.</Text>
                ) : (
                  <ScrollView horizontal showsHorizontalScrollIndicator scrollEnabled={!titleResizing}>
                    <View>
                      {renderTableHeaderRow()}
                      {renderRows(rows, strike)}
                    </View>
                  </ScrollView>
                )}
                {renderQuickAddRow(cat)}
              </GlassCard>
            );
          })}
        </>
      )}
    </ScrollView>
  );

  return (
    <Atmos style={styles.root}>
      {error ? <Text style={styles.error}>{error}</Text> : null}
      {status ? <Text style={styles.status}>{status}</Text> : null}

      {undoBar && (
        <Pressable
          style={styles.undoBar}
          onPress={() => {
            const item = trashItems.find((t) => t.id === undoBar.trashId);
            if (item) void restoreTrashItem(item);
          }}
        >
          <Text style={styles.undoBarText} numberOfLines={1}>
            Deleted "{undoBar.title}"
          </Text>
          <Text style={styles.undoBarAction}>UNDO</Text>
        </Pressable>
      )}

      {screen === 'overview' ? (
        <>
          {/* "My Lists" overview — 1:1 port of web src/app/tasks/page.tsx's
              header (title + PAGE/DOWN/OUT toggle on the left, verification
              Bell + "Add List" on the right) and the Card/ul-divide-y list
              (see that file's return JSX, lines ~1341-1489). */}
          <View style={styles.pageHeaderRow}>
            <View style={styles.pageHeaderLeft}>
              <Text style={styles.pageTitle}>My Lists</Text>
              <View style={styles.modeToggleRow}>
                {(['page', 'down', 'out'] as const).map((mode) => (
                  <Chip
                    key={mode}
                    small
                    label={mode.toUpperCase()}
                    active={overviewMode === mode}
                    onPress={() => setOverviewMode(mode)}
                  />
                ))}
              </View>
            </View>
            <View style={styles.pageHeaderRight}>
              <Pressable
                style={styles.bellIconButton}
                onPress={() => setVerificationOpen(true)}
                accessibilityLabel="Auto-schedule verification"
              >
                <Feather name="bell" size={28} color={t.ivory} />
                {autoScheduleLogs.length > 0 && (
                  <View style={styles.bellRedBadge}>
                    <Text style={styles.bellRedBadgeText}>{autoScheduleLogs.length}</Text>
                  </View>
                )}
              </Pressable>
              <Pressable
                style={styles.addListButton}
                onPress={() =>
                  setPrompt({
                    title: 'Add New List',
                    message: 'Enter a name for your new task list.',
                    placeholder: 'List name',
                    submitLabel: 'Add List',
                    onSubmit: (v) => {
                      setPrompt(null);
                      void addList(v);
                    },
                  })
                }
              >
                <Feather name="plus-circle" size={20} color={t.onGold} style={styles.addListButtonIcon} />
                <Text style={styles.addListButtonText}>Add List</Text>
              </Pressable>
            </View>
          </View>

          {overviewMode === 'page' && (
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              <GlassCard style={styles.tableCard}>
                {chipEntries.map((entry, idx) => {
                  const count = taskCounts[entry.id];
                  const list = lists.find((l) => l.id === entry.id);
                  const isLast = idx === chipEntries.length - 1;
                  return (
                    <Pressable
                      key={entry.id}
                      style={[styles.tableRow, !isLast && styles.tableRowDivider]}
                      onPress={() => {
                        setSelectedListId(entry.id);
                        setScreen('detail');
                      }}
                    >
                      <Text style={styles.tableRowTitle} numberOfLines={1}>
                        {entry.name}
                        {!!count && <Text> - {count} -</Text>}
                      </Text>
                      {list?.autoSchedulerEnabled && (
                        <View style={styles.autoPill}>
                          <Text style={styles.autoPillText}>Auto-Scheduled</Text>
                        </View>
                      )}
                      <Feather name="chevron-right" size={20} color={W.MUTED} />
                      {list && (
                        <Pressable
                          style={styles.tableMenuButton}
                          hitSlop={8}
                          accessibilityLabel={`Settings for list ${list.name}`}
                          onPress={() => openListMenu(list)}
                        >
                          <Feather name="more-vertical" size={16} color={W.MUTED} />
                        </Pressable>
                      )}
                    </Pressable>
                  );
                })}
              </GlassCard>
            </ScrollView>
          )}

          {overviewMode === 'down' && (
            <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
              <GlassCard style={styles.tableCard}>
                {chipEntries.map((entry, idx) => {
                  const count = taskCounts[entry.id];
                  const list = lists.find((l) => l.id === entry.id);
                  const expanded = downExpandedId === entry.id;
                  const isLast = idx === chipEntries.length - 1;
                  return (
                    <View key={entry.id} style={!isLast && !expanded && styles.tableRowDivider}>
                      <View style={styles.accordionRow}>
                        <Pressable
                          style={styles.tableRow}
                          onPress={() => {
                            setSelectedListId(entry.id);
                            setDownExpandedId(expanded ? null : entry.id);
                          }}
                        >
                          <Text style={styles.tableRowTitle} numberOfLines={1}>
                            {entry.name}
                            {!!count && <Text> - {count} -</Text>}
                          </Text>
                          {list?.autoSchedulerEnabled && (
                            <View style={styles.autoPill}>
                              <Text style={styles.autoPillText}>Auto-Scheduled</Text>
                            </View>
                          )}
                          <Feather name={expanded ? 'chevron-up' : 'chevron-down'} size={16} color={W.FOREGROUND} />
                        </Pressable>
                        {list && (
                          <Pressable
                            style={styles.tableMenuButtonAccordion}
                            hitSlop={8}
                            accessibilityLabel={`Settings for list ${list.name}`}
                            onPress={() => openListMenu(list)}
                          >
                            <Feather name="more-vertical" size={16} color={W.MUTED} />
                          </Pressable>
                        )}
                      </View>
                      {expanded && (
                        <View style={styles.accordionBody}>
                          {selectedListId !== entry.id || tasksLoading ? (
                            <View style={styles.center}>
                              <ActivityIndicator color={c.blueText} />
                            </View>
                          ) : (
                            <>
                              {renderToolbar()}
                              {renderTaskBody()}
                            </>
                          )}
                        </View>
                      )}
                    </View>
                  );
                })}
              </GlassCard>
            </ScrollView>
          )}

          {overviewMode === 'out' && (
            <>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.listChipsRow}>
                {chipEntries.map((entry) => {
                  const count = taskCounts[entry.id];
                  const active = selectedListId === entry.id;
                  return (
                    <Pressable
                      key={entry.id}
                      style={[styles.listChip, active && styles.listChipActive]}
                      onPress={() => setSelectedListId(entry.id)}
                      onLongPress={() => {
                        if (!entry.virtual) {
                          const list = lists.find((l) => l.id === entry.id);
                          if (list) openListMenu(list);
                        }
                      }}
                    >
                      <Text style={[styles.listChipText, active && styles.listChipTextActive]} numberOfLines={1}>
                        {entry.name}
                      </Text>
                      {!!count && (
                        <View style={styles.countBubble}>
                          <Text style={styles.countBubbleText}>{count}</Text>
                        </View>
                      )}
                    </Pressable>
                  );
                })}
              </ScrollView>

              {tasksLoading ? (
                <View style={styles.center}>
                  <ActivityIndicator color={c.blueText} />
                </View>
              ) : (
                <>
                  {renderToolbar()}
                  {renderTaskBody()}
                </>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/* Long list-detail view — mirrors web src/app/tasks/[listId]/page.tsx,
              reached by tapping a list in PAGE mode. "‹ OUT" returns to the
              overview (same PAGE/DOWN/OUT mode it was opened from). */}
          <View style={styles.detailHeaderRow}>
            <Pressable style={styles.outButton} onPress={() => setScreen('overview')}>
              <Text style={styles.outButtonText}>‹ OUT</Text>
            </Pressable>
            <Text style={styles.detailTitle} numberOfLines={1}>
              {chipEntries.find((c) => c.id === selectedListId)?.name ?? 'List'}
            </Text>
            {renderHeaderIcons()}
          </View>

          {tasksLoading ? (
            <View style={styles.center}>
              <ActivityIndicator color={c.blueText} />
            </View>
          ) : (
            <>
              {renderToolbar()}
              {renderTaskBody()}
            </>
          )}
        </>
      )}

      <PromptModal state={prompt} onCancel={() => setPrompt(null)} />

      {formModal && (
        <TaskFormModal
          visible={!!formModal}
          isEditing={!!formModal.task}
          initial={buildInitialForm(formModal.task, formModal.presetCategory, formModal.presetParentId)}
          categories={formModalCategories}
          topGroups={topGroups}
          allGroups={groups}
          valueSettings={activeValueSettings}
          etcIntervals={etcIntervals}
          parentOptions={formModalParentOptions}
          otherLists={formModalOtherLists}
          onCancel={() => setFormModal(null)}
          onSave={(value) => void saveTaskForm(value, formModal.task)}
          onDelete={formModal.task ? () => void deleteTask(formModal.task!).then(() => setFormModal(null)) : undefined}
        />
      )}

      <AutoSchedulerModal
        list={schedulerModalList}
        topGroups={topGroups}
        etcIntervals={etcIntervals}
        onCancel={() => setSchedulerModalList(null)}
        onSave={(enabled, groupId, etc, sector) => void saveAutoSchedulerSettings(schedulerModalList!, enabled, groupId, etc, sector)}
        onReschedule={() => {
          if (!schedulerModalList) return;
          Alert.alert('Reschedule list?', `This clears and re-optimizes every eligible task in "${schedulerModalList.name}".`, [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Reschedule', onPress: () => void runForceReschedule(schedulerModalList) },
          ]);
        }}
      />

      <TrashModal
        visible={trashOpen}
        items={trashItems}
        loading={trashLoading}
        onClose={() => setTrashOpen(false)}
        onRestore={(item) => void restoreTrashItem(item)}
        onPermanentDelete={permanentDeleteTrashItem}
        onEmptyAll={emptyTrash}
      />

      <VerificationModal
        visible={verificationOpen}
        userId={userId}
        logs={autoScheduleLogs}
        onClose={() => setVerificationOpen(false)}
        onKeep={(log) => {
          void keepAutoScheduleLog(log.id).then(() => setAutoScheduleLogs((prev) => prev.filter((l) => l.id !== log.id)));
        }}
        onKeepAll={() => {
          const ids = autoScheduleLogs.map((l) => l.id);
          void keepAllAutoScheduleLogs(ids).then(() => setAutoScheduleLogs([]));
        }}
        onReschedule={(log) => setRescheduleLogItem(log)}
      />

      <LogRescheduleModal
        log={rescheduleLogItem}
        onCancel={() => setRescheduleLogItem(null)}
        onSubmit={(dateVal, timeVal) => {
          if (!userId || !rescheduleLogItem) return;
          const scheduledTo = `${dateVal} ${timeVal}`;
          void manuallyRescheduleLoggedTask(rescheduleLogItem, userId, scheduledTo).then(() => {
            setAutoScheduleLogs((prev) => prev.filter((l) => l.id !== rescheduleLogItem.id));
            setStatus(`"${rescheduleLogItem.task_title}" rescheduled to ${scheduledTo}.`);
            setRescheduleLogItem(null);
            void refresh();
          });
        }}
      />
    </Atmos>
  );
}

// ---------- Styles ----------------------------------------------------------

const useStyles = makeThemedStyles(({ t, c, neu }) => {
  const W = webPalette(t);
  return StyleSheet.create({
  root: { flex: 1, padding: 12 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  error: { color: c.danger, marginBottom: 8, fontSize: 13 },
  status: { color: c.blueText, fontWeight: '700', marginBottom: 8, fontSize: 13 },
  emptyText: { color: c.slate, fontStyle: 'italic', textAlign: 'center', marginTop: 24 },
  emptySection: { color: c.slate, fontStyle: 'italic', fontSize: 12, marginBottom: 8 },

  undoBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginBottom: 8,
    gap: 8,
  },
  undoBarText: { flex: 1, color: t.ivory, fontSize: 13, fontWeight: '600' },
  undoBarAction: { color: t.goldBright, fontSize: 13, fontWeight: '900' },

  headerRow: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginBottom: 4 },
  headerIconsRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },

  // ---- Overview screen ("My Lists") — 1:1 port of web src/app/tasks/
  // page.tsx's header + Card/ul-divide-y list. Colors/sizes are the exact
  // Tailwind/shadcn values that page resolves to (see the WEB_* constants
  // and the comment above them near the top of this file).
  pageHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: 8,
    marginBottom: 16,
  },
  pageHeaderLeft: { flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  pageTitle: { fontSize: 22, fontFamily: DISPLAY_FONT, color: t.ivory, letterSpacing: 2.5, textTransform: 'uppercase' },
  pageHeaderRight: { flexDirection: 'row', alignItems: 'center', gap: 8 },


  bellIconButton: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bellRedBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: W.RED,
    borderRadius: 999,
    height: 24,
    width: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bellRedBadgeText: { color: '#ffffff', fontSize: 12, fontWeight: '700' },

  addListButton: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    paddingHorizontal: 24,
    borderRadius: 22,
    backgroundColor: t.gold,
    ...neu.glowPrimary,
  },
  addListButtonIcon: { marginRight: 8 },
  addListButtonText: { fontSize: 14, fontWeight: '800', letterSpacing: 0.4, color: t.onGold },

  tableCard: {
    padding: 0,
    borderRadius: 22,
  },
  tableRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  tableRowDivider: { borderBottomWidth: 1, borderBottomColor: W.BORDER },
  tableRowTitle: { flex: 1, fontSize: 16, fontWeight: '500', color: W.FOREGROUND, marginRight: 16, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  autoPill: {
    borderWidth: 1,
    borderColor: W.GREEN_BORDER,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 2,
    marginRight: 8,
  },
  autoPillText: { fontSize: 12, fontWeight: '600', color: W.GREEN_TEXT },
  tableMenuButton: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center', marginLeft: 4 },
  tableMenuButtonAccordion: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center', marginRight: 8 },
  accordionRow: { flexDirection: 'row', alignItems: 'center', paddingRight: 8 },

  accordionBody: {
    backgroundColor: c.limeSoft,
    padding: 10,
  },

  // ---- Long detail screen ("‹ OUT" back to overview) ----
  detailHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  outButton: {
    borderRadius: radii.pill,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderWidth: 1, borderColor: t.navyLine,
  },
  outButtonText: { fontSize: 12, fontWeight: '900', color: c.blueText },
  detailTitle: { flex: 1, fontSize: 17, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },

  trashButton: { padding: 6, marginRight: 2 },
  trashIcon: { fontSize: 20 },
  bellButton: { padding: 6 },
  bellIcon: { fontSize: 22 },
  bellBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: c.danger,
    borderRadius: radii.pill,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  bellBadgeText: { color: '#ffffff', fontSize: 9, fontWeight: '900' },

  helperText: { fontSize: 11, color: c.slate, marginTop: 4, fontStyle: 'italic' },
  rescheduleButton: {
    marginTop: 16,
    borderWidth: 1, borderColor: t.gold,
    borderRadius: 22,
    paddingVertical: 12,
    alignItems: 'center',
  },
  rescheduleButtonDisabled: { opacity: 0.4 },
  rescheduleButtonText: { fontWeight: '900', color: c.blueText, fontSize: 13 },

  logRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
    paddingVertical: 10,
    gap: 8,
  },
  logRowText: { flex: 1 },
  logTitle: { fontSize: 14, fontWeight: '700', color: c.danger, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  logMeta: { fontSize: 11, color: c.slate, marginTop: 2 },
  logRowActions: { flexDirection: 'row', gap: 6 },
  logActionBtn: {
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1, borderColor: t.navyLine,
  },
  logActionText: { fontSize: 11, fontWeight: '800', color: c.blueText },
  logActionTextDanger: { color: c.danger },

  // Verification bell: List/Calendar toggle
  verifHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 8,
  },

  // Verification bell: Calendar view
  calWrap: { maxHeight: 420 },
  calNavRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, marginBottom: 6 },
  calNavBtn: {
    width: 26,
    height: 26,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1, borderColor: t.navyLine,
  },
  calNavBtnText: { fontSize: 16, fontWeight: '900', color: c.blueText },
  calTodayBtn: {
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: 1, borderColor: t.navyLine,
  },
  calTodayBtnText: { fontSize: 11, fontWeight: '800', color: c.slate },
  calDateLabel: { fontSize: 12, fontWeight: '800', color: c.text, marginLeft: 4 },
  calSelectedRow: {
    marginHorizontal: 16,
    marginBottom: 6,
    padding: 8,
    borderRadius: radii.tile,
    backgroundColor: t.navyRaised,
    borderWidth: 1,
    borderColor: t.lapsed,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
  },
  calSelectedTitle: { flex: 1, fontSize: 12, fontWeight: '800', color: c.danger, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  calSlotScroll: { paddingHorizontal: 16 },
  calSlotScrollContent: { paddingBottom: 16 },
  calSlotRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
    paddingVertical: 6,
    gap: 8,
    minHeight: 30,
  },
  calSlotLabel: { width: 64, fontSize: 10, fontWeight: '700', color: c.slate },
  calSlotItems: { flex: 1, gap: 4 },
  calSlotEmpty: { height: 16 },
  calItem: {
    backgroundColor: c.lime,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  calItemText: { fontSize: 11, fontWeight: '600', color: c.text },
  calItemVerif: {
    backgroundColor: t.navyRaised,
    borderWidth: 1,
    borderColor: t.lapsed,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  calItemVerifText: { fontSize: 11, fontWeight: '800', color: c.danger },

  listChipsRow: { flexGrow: 0, marginBottom: 10 },
  listChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: radii.pill,
    paddingHorizontal: 12,
    paddingVertical: 7,
    marginRight: 8,
    maxWidth: 160,
    borderWidth: 1, borderColor: t.navyLine,
  },
  listChipActive: { backgroundColor: t.gold, borderColor: t.goldBright, ...neu.glowPrimary },
  listChipText: { fontSize: 13, fontWeight: '700', color: c.text },
  listChipTextActive: { color: t.onGold },
  countBubble: {
    marginLeft: 6,
    backgroundColor: t.gold,
    borderRadius: radii.pill,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  countBubbleText: { color: t.onGold, fontSize: 10, fontWeight: '900' },

  // ---- Task table toolbar/section/row chrome — flat web palette (white,
  // hairline #e5e7eb borders, #293341 foreground, #6b7280 muted, #1e88e5
  // primary), 1:1 port of web's outline <Button>/<Card>/<Table> chrome.
  // Zero neu.* shadows, zero pill chips, zero rounded floating cards.
  toolbar: { marginBottom: 10, gap: 6 },
  toolbarButtons: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  outlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1, borderColor: t.navyLine,
  },
  outlineButtonIcon: { marginRight: 6 },
  outlineButtonText: { fontSize: 12, fontWeight: '600', color: W.PRIMARY },

  body: { flex: 1 },
  bodyContent: { paddingBottom: 24 },

  sectionCard: {
    padding: 0,
    borderRadius: 22,
    marginBottom: 10,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: W.BORDER,
    gap: 6,
  },
  sectionHeaderText: { fontSize: 15, fontWeight: '600', color: W.FOREGROUND },
  sectionCountBadge: {
    fontSize: 11,
    fontWeight: '600',
    color: W.MUTED,
    backgroundColor: c.glassFillStrong,
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  quickAddRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    margin: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1, borderColor: t.navyLine,
    borderRadius: 14,
    backgroundColor: t.glass,
  },
  quickAddInput: {
    flex: 1,
    fontSize: 13,
    color: W.FOREGROUND,
    padding: 0,
  },

  // ---- Flat table header/row (renderTableHeaderRow / TaskTableRow) ----
  trHeader: {
    flexDirection: 'row',
    backgroundColor: c.limeSoft,
    borderBottomWidth: 1,
    borderBottomColor: W.BORDER,
  },
  thCell: { paddingHorizontal: 4, paddingVertical: 8, justifyContent: 'center' },
  thText: { fontSize: 11, fontWeight: '600', color: W.MUTED },
  thTextActive: { color: W.FOREGROUND },
  // Title header holds both the sort label and the drag-to-resize handle.
  thTitleCell: { flexDirection: 'row', alignItems: 'center', position: 'relative', paddingRight: 12 },
  thTitleLabelHit: { flex: 1, justifyContent: 'center' },
  thResizeHandle: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thResizeGrip: { width: 3, height: 16, borderRadius: 2, backgroundColor: W.PRIMARY },
  trRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: W.BORDER,
  },
  trRowSubtask: { backgroundColor: c.limeSoft },
  trRowDone: { opacity: 0.5 },
  tdCell: { paddingHorizontal: 4, paddingVertical: 8, justifyContent: 'center' },
  tdMuted: { fontSize: 12, color: W.MUTED },
  tdText: { color: W.FOREGROUND },
  tdTitleCell: { flexDirection: 'row', alignItems: 'center' },
  tdTitleText: { flex: 1, fontSize: 13, fontWeight: '500', color: W.FOREGROUND, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  tdTitleDone: { textDecorationLine: 'line-through', color: W.MUTED, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  expandBtn: { width: 18, alignItems: 'center', justifyContent: 'center' },
  expandSpacer: { width: 18 },
  subtaskIcon: { width: 18, textAlign: 'center' },
  checkboxBox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1, borderColor: t.navyLine,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.navyRaised,
  },
  checkboxBoxOn: { backgroundColor: W.PRIMARY, borderColor: W.PRIMARY },
  priorityPill: {
    minWidth: 26,
    height: 20,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  priorityPillLow: { backgroundColor: t.onPace },
  priorityPillMed: { backgroundColor: t.behind },
  priorityPillHigh: { backgroundColor: t.lapsed },
  priorityPillText: { fontSize: 11, fontWeight: '700', color: t.onGold },
  addInput: {
    height: 26,
    width: 44,
    borderWidth: 1, borderColor: t.navyLine,
    borderRadius: 8,
    textAlign: 'center',
    fontSize: 12,
    color: W.FOREGROUND,
    backgroundColor: t.navyRaised,
    paddingVertical: 0,
    paddingHorizontal: 2,
  },

  // Chips (shared by pickers)
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4 },

  // Generic prompt / modal chrome
  backdrop: { flex: 1, backgroundColor: c.overlay, justifyContent: 'flex-end' },
  promptBox: {
    margin: 20,
    borderRadius: 22,
    padding: 16,
    alignSelf: 'stretch',
  },
  promptTitle: { fontSize: 15, fontWeight: '900', color: c.text, marginBottom: 10, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  promptMessage: { fontSize: 12, color: c.slate, marginTop: -6, marginBottom: 10 },
  promptButtons: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
  promptBtn: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 999, borderWidth: 1, borderColor: t.gold },
  promptBtnText: { fontSize: 13, fontWeight: '800', color: t.goldBright },

  // Task form sheet
  sheet: {
    padding: 0,
    borderRadius: 28,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    maxHeight: '92%',
    paddingTop: 14,
  },
  sheetTitle: { letterSpacing: 2, fontSize: 16, fontWeight: '900', color: c.text, paddingHorizontal: 16, marginBottom: 6, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  sheetScroll: { paddingHorizontal: 16 },
  sheetScrollContent: { paddingBottom: 16 },
  sheetFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
  },
  field: { marginTop: 12 },
  fieldLabel: { fontSize: 10.5, fontWeight: '800', letterSpacing: 1.6, textTransform: 'uppercase', color: t.ivoryFaint, marginBottom: 4 },
  textArea: { minHeight: 70, textAlignVertical: 'top' },
  deleteRow: { marginTop: 18, alignItems: 'center', paddingVertical: 10 },
  deleteRowText: { color: c.danger, fontWeight: '800', fontSize: 13 },
  modeToggleRow: { flexDirection: 'row', gap: 4 },
  verifToggleRow: { flexDirection: 'row', gap: 6 },
  });
});
